/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { CheckCheck, ChevronDown, ChevronLeft, ChevronRight, Download, Loader2, Save, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useViewParams } from "../../hooks/useViewParams";
import { DataTable, buildTimeSeriesColumns, type ColumnDef, type HeaderGroup } from "../../components/DataTable";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { WarningIcon } from "../../components/ui/warning-icon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useSystemMessages } from "../../hooks/useSystemMessages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { EmptyState } from "../../components/ui/empty-state";
import { ErrorState } from "../../components/ui/error-state";
import { LoadingState } from "../../components/ui/loading-state";
import { TableCell, TableHead } from "../../components/ui/table";
import { ViewControls, type FilterEntry } from "../../components/ViewControls";
import { useFirstReceiverMappingGaps } from "../../hooks/useFirstReceiverMappingGaps";
import { useMappingGaps } from "../../hooks/useMappingGaps";
import { useRunSelector } from "../../hooks/useRunSelector";
import { formatRunOptionLabel } from "../../hooks/runSelectionState";
import { buildExportMetaRows, sortLabel } from "../../lib/exportMeta";
import { STRING_COLUMN_TYPES, useSchema } from "../../hooks/useSchema";
import { useSkuMapping, type SkuCol } from "../../hooks/useSkuMapping";
import { useViewTransform } from "../../hooks/useViewTransform";
import { PageHeader } from "../../layouts/DashboardLayout/PageHeader";
import { ShipmentsTabs } from "../../components/ShipmentsTabs";
import { apolloClient } from "../../services/backend_gateway/apollo-client";
import { viewTransformsClient } from "../../services/viewtransforms/apollo-client";
import {
  CREATE_MY_VIEW_TRANSFORM_VT,
  type CreateMyViewTransformVTInput,
  type CreateMyViewTransformVTResult,
} from "../../services/viewtransforms/mutations/create_my_view_transform";
import {
  GetRetailerSupplyEditsCsvUploadUrlDocument,
  MarkRetailerSupplyAsReadyDocument,
  SaveRetailerSupplyEditsDocument,
  SkuGrain,
  UploadRetailerSupplyEditsCsvDocument,
  type RetailerSupplyEditInput,
} from "../../services/__generated__/graphql";
import { tableStyles } from "../../styles";
import { theme } from "../../styles/theme/theme";
import { formatDisplayDate, isPastOrCurrentPeriod, sortDateColumns } from "../../utils/date";
import { formatCurrencyOrNull, formatWOS, toColumnLabel } from "../../utils/format";
import { buildVtParams } from "../../utils/viewTransformParams";
import { calculateColumnWidth, type WidthInput } from "../../utils/tableWidth";
import { type SortCriteria, getNextSortState } from "../../types/sort";
import { ApplyShipmentEditsButton } from "./ApplyShipmentEditsButton";
import { aggregateOneFirstReceiver, MEASURE, recalcMeasures, suggestParityRun, type ParityRunCell } from "./calculations";
import {
  CHECKBOX_COL_W,
  MARKED_READY_COL_W,
  MEASURE_KEYS,
  ACTUALS_MEASURE_KEYS,
  FALLBACK_SUPPRESSED_KEYS,
  STATUS_COL_W,
  STATUS_CONFIG,
  SKU_COL_W,
  getMeasureLabel,
  wosCellBg,
} from "./constants";
import { useExceptionTypes } from "../../hooks/useExceptionTypes";
import { PortalTooltip } from "./PortalTooltip";
import { s } from "./RetailerSupplyView.styles";
import { SkuDetailRow as SkuDetailRowComponent } from "./SkuDetailRow";
import { SkuRow as SkuRowComponent } from "./SkuRow";
import type { FirstReceiverSummary, RowStatus, SkuRow, SummarySkuRow } from "./types";
import {
  computePeriodColumns,
  materializeSkuRows,
  type SkuRowsPostProcess,
  type SkuRowsPostProcessResult,
} from "./skuRowMaterialize";
import { effectiveSavedForParity, formatGrainValue, hasAnyNetOverride, sameRounded } from "./utils";
import { MEASURE_FORMAT } from "../../lib/supplyWalkMeasures";

const FILTER_CONFIG = [
  { col: "first_receiver", param: "filterFirstReceiver" as const },
  // CHO-478 stage 3: the old retailer dropdown is now keyed on planning_level
  // to match the bottom-table column. For buckets 1 + 3 the values are FR
  // names (1:1 with retailer for bucket 1); for bucket 2 they're "<FR> - <DC>"
  // composites once the engine re-runs.
  { col: "planning_level", param: "filterPlanningLevel" as const, label: "Planning Level" },
  { col: "planner_name", param: "filterPlannerName" as const },
] as const;

// Client-side boolean filters derived per-row from VT output. Unlike
// FILTER_CONFIG above, these don't push through to the VT query — the row
// predicate runs on allSkuRows after the data lands.
const STATIC_FILTER_CONFIG = [
  // True iff iohSource is anything other than "netsuite_fallback" — i.e. the
  // engine had a real Alloy/other inventory anchor rather than the fallback
  // starting IOH.
  { col: "_has_inventory_data", param: "filterHasInventoryData" as const, label: "Has Inventory Data" },
  // True iff at least one forecast period (is_actuals == 0) has nonzero
  // forecast_sales — i.e. the engine sees demand to plan against.
  { col: "_has_sales_forecast", param: "filterHasSalesForecast" as const, label: "Has Sales Forecast" },
] as const;

const BOOLEAN_FILTER_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// unfulfilled_demand and open_shipments_placed stay in MEASURE_KEYS for width
// measurement, but are hidden from the detail walk (see getSkuDetailKeys), so
// excluded here too — picking them as the aggregate would leave the expanded
// row with no matching detail row to land on.
const AGGREGATE_OPTION_EXCLUDED = new Set(["unfulfilled_demand", "open_shipments_placed"]);
const AGGREGATE_OPTIONS = MEASURE_KEYS
  .filter((k) => !AGGREGATE_OPTION_EXCLUDED.has(k))
  .map((k) => ({
    value: k,
    label: getMeasureLabel(k, "Week"),
  }));

type SkuGrainValue = "shipping" | "vividly" | "stick";

const SKU_GRAIN_OPTIONS: { value: SkuGrainValue; label: string }[] = [
  { value: "shipping", label: "Shipping case" },
  { value: "vividly", label: "Vividly product" },
  { value: "stick", label: "Stick" },
];

const GRAIN_TO_SKU_COL: Record<SkuGrainValue, SkuCol> = {
  shipping: "shipping_sku",
  vividly: "vividly_product",
  stick: "stick_sku",
};

const GRAIN_TO_ENUM: Record<SkuGrainValue, SkuGrain> = {
  shipping: SkuGrain.Shipping,
  vividly: SkuGrain.Vividly,
  stick: SkuGrain.Stick,
};

const defaultViewParams = {
  filterFirstReceiver: [] as string[],
  filterPlanningLevel: [] as string[],
  filterPlannerName: [] as string[],
  filterSku: [] as string[],
  filterAlertTypes: [] as string[],
  filterHasInventoryData: ["Yes"] as string[],
  filterHasSalesForecast: ["Yes"] as string[],
  topAggregateBy: "ending_days_of_supply" as string,
  bottomAggregateBy: "ending_days_of_supply" as string,
  skuGrain: "vividly" as SkuGrainValue,
  // Toggle: when false, the viewtransform skips current_edits.json and
  // returns the engine's raw shipment plan (CHO-454). Useful for diagnosing
  // whether unexpected WOS patterns come from engine policy or saved edits.
  applyEdits: true,
  // CHO-678 Phase C (?lazyDetail). DEFAULT ON: the page never loads the full
  // ~6.7MB ui_supply_walk_for_editing upfront — both tables run off the lean
  // summary VTs and SKU detail is fetched on expand. `?lazyDetail=false` is the
  // escape hatch back to the full-load path.
  //
  // CAVEAT (pre-prod): the summary VTs are exact only with NO saved planner
  // edits — with saved edits their aggregates are engine-only and the top
  // "edited" status reflects only live session edits. Correct on a no-saved-edit
  // env (greg); the saved-edit summary recalc is the remaining follow-up before
  // this is correct for an env that carries saved edits.
  lazyDetail: true,
};

export const RetailerSupplyView = () => {
  const [selectedFirstReceiver, setSelectedFirstReceiver] = useState<string | null>(null);
  const { params, setParam, setParams } = useViewParams(defaultViewParams);
  const [editedMeasures, setEditedMeasures] = useState<Map<string, Record<string, number[]>>>(
    new Map()
  );
  const [selectedFirstReceivers, setSelectedFirstReceivers] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [clearedCells, setClearedCells] = useState<Map<string, Set<number>>>(new Map());
  // Per-row snapshot of the editedMeasures + clearedCells state captured
  // immediately before handleRevertRows clears the row. While the snapshot
  // exists, SkuDetailRow shows a "Restore" button in place of the revert
  // button — letting the planner undo the clear before saving. The map is
  // dropped on save, run change, applyEdits toggle, and discard-switch (i.e.
  // any moment the editedMeasures/clearedCells buffers are themselves reset).
  const [revertSnapshots, setRevertSnapshots] = useState<
    Map<string, { measures: Record<string, number[]> | undefined; clears: Set<number> | undefined }>
  >(new Map());
  // Live mirrors of buffered edit state, read by handleRevertRows (to
  // snapshot pre-revert) and handleRestoreRow (to read the snapshot). Refs,
  // not deps, so the surrounding useCallbacks keep stable identity and the
  // SkuDetailRow memo stays effective.
  const editedMeasuresRef = useRef(editedMeasures);
  editedMeasuresRef.current = editedMeasures;
  const clearedCellsRef = useRef(clearedCells);
  clearedCellsRef.current = clearedCells;
  const revertSnapshotsRef = useRef(revertSnapshots);
  revertSnapshotsRef.current = revertSnapshots;
  const [activePromo, setActivePromo] = useState<{ rowId: string; periodIdx: number } | null>(null);
  const [promoAnchor, setPromoAnchor] = useState<HTMLElement | null>(null);
  const [actualsExpanded, setActualsExpanded] = useState(false);
  const [topSort, setTopSort] = useState<SortCriteria[]>([]);
  const [skuSort, setSkuSort] = useState<SortCriteria[]>([]);
  // Bottom-panel nesting: which SKUs are expanded to reveal their PL rows.
  // The third level (per-PL measure walk) keeps using DataTable's internal
  // expansion — each PL row still owns its own measure walk as before.
  const [expandedSkus, setExpandedSkus] = useState<Set<string>>(new Set());
  // CHO-678 OQ3 Phase C: SKUs the planner has expanded in the COLLAPSED bottom
  // table. Drives a lazy per-SKU detail fetch (detailVt below) — distinct from
  // `expandedSkus`, which is the full-path SKU→PL nesting toggle.
  const [expandedSummarySkus, setExpandedSummarySkus] = useState<Set<string>>(new Set());
  const toggleSummarySku = useCallback((sku: string) => {
    setExpandedSummarySkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  }, []);
  const toggleSku = useCallback((sku: string) => {
    setExpandedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  }, []);
  const cellEditModeRef = useRef(false);
  const csvFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCsvUploading, setIsCsvUploading] = useState(false);
  const { addMessage } = useSystemMessages();

  // Pointer to the cell that triggered a parity-run suggestion — just the
  // (row, week) pair. The actual `cells` shown by the chip are derived on
  // every render from current row.measures + editedMeasures (see useMemo
  // below). Storing only the anchor — not snapshotted cells — means a VT
  // refetch (which can replace the engine baseline under us) refreshes the
  // suggestion instead of leaving a chip pointing at stale numbers.
  const [parityHintAnchor, setParityHintAnchor] = useState<{
    rowId: string;
    editedWeekIdx: number;
  } | null>(null);

  // Close promo popup on any scroll in any ancestor — nested scroll containers
  // in this view make viewport-only IntersectionObserver insufficient.
  useEffect(() => {
    if (!activePromo || !promoAnchor) return;
    const close = () => {
      setActivePromo(null);
      setPromoAnchor(null);
    };
    const targets: (Element | Window)[] = [window];
    let node: Element | null = promoAnchor.parentElement;
    while (node) {
      const { overflowX, overflowY } = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(overflowX) || /(auto|scroll|overlay)/.test(overflowY)) {
        targets.push(node);
      }
      node = node.parentElement;
    }
    for (const t of targets) t.addEventListener("scroll", close, { passive: true, capture: true });
    return () => {
      for (const t of targets) t.removeEventListener("scroll", close, { capture: true } as EventListenerOptions);
    };
  }, [activePromo, promoAnchor]);

  // ── Paired horizontal scroll ─────────────────────────────────────────────
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const scrollingRef = useRef<"top" | "bottom" | null>(null);

  const handleScroll = useCallback((source: "top" | "bottom") => {
    if (scrollingRef.current && scrollingRef.current !== source) return;
    scrollingRef.current = source;
    const from = source === "top" ? topScrollRef.current : bottomScrollRef.current;
    const to = source === "top" ? bottomScrollRef.current : topScrollRef.current;
    if (from && to) to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => { scrollingRef.current = null; });
  }, []);

  const onTopScroll = useCallback(() => handleScroll("top"), [handleScroll]);
  const onBottomScroll = useCallback(() => handleScroll("bottom"), [handleScroll]);

  const { titleAddon, runSettingsJson, selectedRun } = useRunSelector();
  const navigate = useNavigate();
  // Mapping-gap alert: lights up the header when any Mappings-tab source has
  // incomplete rows (the signal that replaces the old inline "[UNMAPPED]" rows).
  const mappingGaps = useMappingGaps();
  // Per-first-receiver mapping gaps for this run — drives the alert icon next to
  // each first receiver in the "Weeks of Supply by First Receiver" panel.
  const firstReceiverGaps = useFirstReceiverMappingGaps(runSettingsJson);

  // Edits are keyed by `${planning_level}::${sku}` and outlive runSettings
  // changes. Without this reset, switching runs would silently overlay run A's
  // edits onto run B's rows and persist them against run B's input_id.
  // Toggling applyEdits also drops any session edits — they were computed
  // against the edits-applied baseline and would visually wreck the
  // engine-only walk.
  useEffect(() => {
    setEditedMeasures(new Map());
    setClearedCells(new Map());
    setRevertSnapshots(new Map());
  }, [runSettingsJson, params.applyEdits]);

  // Confirmation dialog for toggling applyEdits OFF while there are unsaved
  // edits. The Switch is controlled, so we just don't call setParam until the
  // user picks an action.
  const [confirmEngineSwitch, setConfirmEngineSwitch] = useState(false);

  // The chip prompts against an unsaved session edit. When the edit buffer
  // gets fully wiped (save success, run change), the prompt has nothing to
  // act on — drop it. Mark Ready leaves editedMeasures intact, so the
  // useMemo derivation refreshes the suggestion against the post-refetch
  // baseline instead of going through this path.
  useEffect(() => {
    if (editedMeasures.size === 0) setParityHintAnchor(null);
  }, [editedMeasures]);

  const skuCol = GRAIN_TO_SKU_COL[params.skuGrain];
  // CHO-678 Phase C feature flag: collapsed-primary, lazy detail-on-expand.
  const lazyDetail = params.lazyDetail === true;

  const filters: FilterEntry[] = useMemo(() => {
    const entries: FilterEntry[] = [];
    for (const { col, param } of FILTER_CONFIG) {
      if (params[param].length > 0) entries.push([col, "in", params[param]]);
    }
    if (params.filterSku.length > 0) entries.push([skuCol, "in", params.filterSku]);
    return entries;
  }, [params.filterFirstReceiver, params.filterPlanningLevel, params.filterPlannerName, params.filterSku, skuCol]);

  // Drilldown (selecting an FR in the top table) scopes the collapsed bottom +
  // detail fetches to that FR — pushed server-side as an extra filter so the
  // sku/detail VTs return only that FR's population (CHO-678 Phase C).
  const filtersWithDrilldown: FilterEntry[] = useMemo(
    () => (selectedFirstReceiver ? [...filters, ["first_receiver", "in", [selectedFirstReceiver]]] : filters),
    [filters, selectedFirstReceiver],
  );

  const vtParams = useMemo(() => {
    // CHO-478 stage 3: bottom-table grain is (planning_level, sku). For
    // buckets 1 + 3 planning_level == first_receiver so there's one row per
    // (FR, sku); for bucket 2 it's "<FR> - <DC>" so each DC gets its own row.
    // `retailer` is intentionally dropped from segments — the engine plans
    // at planning_level grain, and rolling retailers up under each
    // planning_level matches the colleague's "single edit at FR grain" rule
    // for collapsed FRs (Aldi etc.). Shipping grain still carries
    // vividly_product as an extra dim for the sku-level rows.
    const skuSegments =
      params.skuGrain === "shipping"
        ? ["shipping_sku", "vividly_product"]
        : [skuCol];
    return buildVtParams({
      segments: [
        "first_receiver",
        "planning_level",
        ...skuSegments,
        "target_weeks_of_supply",
      ],
      filters,
      extra: {
        aggregate_by: "ending_inventory",
        sku_grain: params.skuGrain,
        apply_edits: params.applyEdits,
        selected_exceptions: params.filterAlertTypes,
      },
    });
  }, [filters, params.skuGrain, skuCol, params.applyEdits, params.filterAlertTypes]);

  // CHO-678 Phase 7 Step 2: materialize SkuRow[] inside the parquet worker, in
  // the same pass it parses this VT's table — so the main thread receives ready
  // rows with no second Arrow IPC round-trip. Memoized so the parse callback
  // isn't recreated every render; a grain change flows through vtParams (new
  // fetch) carrying the matching descriptor.
  const skuRowsPostProcess = useMemo<SkuRowsPostProcess>(
    () => ({ kind: "skuRows", skuCol, skuGrain: params.skuGrain }),
    [skuCol, params.skuGrain],
  );

  // CHO-678 OQ3 Variant 2 (progressive load): fire the lean FR-summary VT in
  // PARALLEL with the full VT. It returns one row per (first_receiver, measure)
  // with weekly period columns (~82× smaller, ~0.55s Lambda vs ~3.4s full) so
  // the top panel can paint before the full per-SKU walk + reshape lands. The
  // full VT backfills the bottom panel + edit/sort/drilldown interactivity. No
  // postProcess (the summary is already wide-by-FR, no SkuRow[] to materialize).
  const summaryVtParams = useMemo(
    () =>
      buildVtParams({
        segments: ["first_receiver"],
        // Top table reflects the active multiselect filters, but NOT drilldown
        // (drilldown filters the top *view* to one FR client-side; the VT still
        // provides every FR so you can clear the drilldown without a refetch).
        filters,
        extra: { sku_grain: params.skuGrain, aggregate_by: "ending_inventory" },
      }),
    [filters, params.skuGrain],
  );
  const summaryVt = useViewTransform({
    transformId: "ui_supply_walk_summary",
    runSettings: runSettingsJson || undefined,
    initialParams: summaryVtParams,
  });

  // CHO-678 OQ3 § two-tier collapsed: the SKU-grain sibling of the FR summary.
  // Fires in parallel and returns one row per (sku, measure) with weekly period
  // columns (~14× smaller than the full output, ~71-215ms local) so the BOTTOM
  // panel can paint its collapsed "SKU header" rows immediately — both tables
  // populated at the collapsed grain before the full per-SKU walk lands. The
  // full VT still backfills the expandable detail + edit interactivity.
  const skuSummaryVtParams = useMemo(
    () =>
      buildVtParams({
        segments: [skuCol],
        // Collapsed bottom respects multiselect filters + drilldown (scopes to
        // the selected FR's SKUs server-side).
        filters: filtersWithDrilldown,
        extra: { sku_grain: params.skuGrain, aggregate_by: "ending_inventory" },
      }),
    [params.skuGrain, skuCol, filtersWithDrilldown],
  );
  const skuSummaryVt = useViewTransform({
    transformId: "ui_supply_walk_sku_summary",
    runSettings: runSettingsJson || undefined,
    initialParams: skuSummaryVtParams,
  });

  // CHO-678 OQ3 Phase C: lazy per-SKU DETAIL fetch. The same full transform
  // (`ui_supply_walk_for_editing`) but filtered to ONLY the expanded SKUs, so
  // the bottom table can show a SKU's PL rows + measure walk on expand WITHOUT
  // the full ~6.7MB load. v1 refetches the (small) union of expanded SKUs on
  // each expand; `undefined` params keep it dormant until the first expand.
  const detailVtParams = useMemo(() => {
    if (expandedSummarySkus.size === 0) return undefined;
    const skuSegments =
      params.skuGrain === "shipping" ? ["shipping_sku", "vividly_product"] : [skuCol];
    return buildVtParams({
      segments: ["first_receiver", "planning_level", ...skuSegments, "target_weeks_of_supply"],
      filters: [...filtersWithDrilldown, [skuCol, "in", [...expandedSummarySkus]]],
      extra: {
        aggregate_by: "ending_inventory",
        sku_grain: params.skuGrain,
        apply_edits: params.applyEdits,
        selected_exceptions: params.filterAlertTypes,
      },
    });
  }, [expandedSummarySkus, filtersWithDrilldown, params.skuGrain, skuCol, params.applyEdits, params.filterAlertTypes]);

  const detailVt = useViewTransform({
    transformId: "ui_supply_walk_for_editing",
    runSettings: runSettingsJson || undefined,
    initialParams: detailVtParams,
    postProcess: skuRowsPostProcess,
  });

  const vt = useViewTransform({
    transformId: "ui_supply_walk_for_editing",
    runSettings: runSettingsJson || undefined,
    // CHO-617 Phase 1: fire immediately without waiting on the backendgateway
    // runs query (~3s on the critical path). With runSettings omitted the VT
    // Lambda resolves the latest successful run server-side (_resolve_run_settings)
    // and returns its canonical runSettingsUsed. On the common path that equals
    // the run the selector defaults to, so useViewTransform's guard skips the
    // re-fire once runSettingsJson resolves; if the user has a non-latest run
    // selected, the resolved value differs and the hook re-fires with it.
    // CHO-678 Phase C: when the lazyDetail flag is on, the full ~6.7MB VT is NOT
    // loaded upfront (undefined params keep the hook dormant) — both tables run
    // off the summary VTs and detail is fetched per-expand via detailVt.
    initialParams: lazyDetail ? undefined : vtParams,
    postProcess: skuRowsPostProcess,
  });

  // ── Filter options (from raw dataset schema) ─────────────────────────────

  const schema = useSchema({
    runSettings: runSettingsJson || "",
    datasetName: "ui_supply_walk_for_editing",
    folder: "outputs",
  });

  // Cross-grain sku mapping so a selection in the SKU filter survives when
  // the user flips Display by — e.g. {vividly_product: ["X"]} becomes the
  // corresponding shipping_sku set when grain switches to "shipping".
  const skuMapping = useSkuMapping(runSettingsJson, "ui_supply_walk_for_editing");

  // Alerts dropdown options come from the engine's per-run sidecar
  // (ui_supply_walk_exception_types.json) so registering a new prototype in
  // exception_definitions.py surfaces here without a frontend deploy. The
  // hook falls back to a single low_inventory option on legacy runs or auth
  // misses — see useExceptionTypes for the contract.
  const { data: exceptionTypes } = useExceptionTypes(runSettingsJson);
  const alertOptions = useMemo(
    () => exceptionTypes.map((t) => ({ value: t.name, label: t.label })),
    [exceptionTypes],
  );


  const filterColumns = useMemo(() => {
    const schemaCols = FILTER_CONFIG.map((cfg) => {
      const profile = schema.columnProfiles.find(
        (p) => p.name === cfg.col && STRING_COLUMN_TYPES.has(p.type)
      );
      const options = (profile?.uniqueValues ?? []).map((v) => ({ value: v, label: v }));
      const label = "label" in cfg && cfg.label ? cfg.label : toColumnLabel(cfg.col);
      return { col: cfg.col as string, param: cfg.param, label, options };
    });
    const staticCols = STATIC_FILTER_CONFIG.map((cfg) => ({
      col: cfg.col as string,
      param: cfg.param,
      label: cfg.label,
      options: BOOLEAN_FILTER_OPTIONS,
    }));
    return [...schemaCols, ...staticCols];
  }, [schema.columnProfiles]);

  // Options for the SKU filter come from the schema profile of whichever sku
  // column matches the current Display by grain. When the grain flips, the
  // selection is converted to the equivalent sku set in the new column (see
  // DisplayBy onChange + useSkuMapping), so values here always belong to the
  // active column.
  const skuFilterOptions = useMemo(() => {
    const profile = schema.columnProfiles.find(
      (p) => p.name === skuCol && STRING_COLUMN_TYPES.has(p.type)
    );
    return (profile?.uniqueValues ?? []).map((v) => ({ value: v, label: v }));
  }, [schema.columnProfiles, skuCol]);

  // ── Period columns from VT output ─────────────────────────────────────────

  const periodColumns = useMemo(() => {
    if (!vt.arrowTable || vt.arrowTable.numRows === 0) return [];
    // Shared with the parquet worker (skuRowMaterialize.computePeriodColumns) so
    // the worker materializes per-period measure arrays in the SAME order the
    // main thread indexes them by.
    return computePeriodColumns(vt.columns);
  }, [vt.arrowTable, vt.columns]);

  // Build actuals period set from the is_actuals measure in the VT data
  // (driven by engine status column, not calendar date)
  const pastPeriodSet = useMemo(() => {
    const set = new Set<string>();
    const table = vt.arrowTable;
    if (!table || table.numRows === 0) {
      // Fallback to calendar-based if no data
      for (const p of periodColumns) {
        if (isPastOrCurrentPeriod(p, "Week")) set.add(p);
      }
      return set;
    }
    // is_actuals is emitted per segment-tuple, so different rows can have
    // different past coverage — e.g. when an Inventory Data Source filter
    // selects a population where some segment-tuples are Forecast-only.
    // Union across ALL is_actuals rows (not just the first) so the chevron
    // surfaces whenever any tuple has Actuals on a given period. Per-period
    // short-circuit keeps the inner loop bounded.
    const measureCol = table.getChild("measure");
    if (!measureCol) return set;
    // Hoist the period column vectors out of the row loop. table.getChild(name)
    // is an O(numColumns) name lookup, so calling it inside the row×period loop
    // made this O(rows × periods × columns) (CHO-617 Phase 7 §7E: ~0.5 s).
    const periodCols = periodColumns.map((p) => ({ name: p, col: table.getChild(p) }));
    for (let i = 0; i < table.numRows; i++) {
      if (String(measureCol.get(i)) !== "is_actuals") continue;
      for (const { name, col } of periodCols) {
        if (set.has(name)) continue;
        if (col && Number(col.get(i)) > 0) set.add(name);
      }
      // Every period already has actuals — nothing left to discover.
      if (set.size === periodColumns.length) break;
    }
    return set;
  }, [vt.arrowTable, periodColumns]);

  // Visible period columns: when actuals are collapsed, only show forecast periods
  const visiblePeriodColumns = useMemo(
    () => actualsExpanded ? periodColumns : periodColumns.filter((c) => !pastPeriodSet.has(c)),
    [periodColumns, pastPeriodSet, actualsExpanded]
  );
  const visiblePeriodSet = useMemo(() => new Set(visiblePeriodColumns), [visiblePeriodColumns]);

  const hasActuals = useMemo(() => periodColumns.some((c) => pastPeriodSet.has(c)), [periodColumns, pastPeriodSet]);

  // ── Summary-VT derivations (CHO-678 OQ3 Variant 2) ────────────────────────
  // These mirror the full-VT period/past/aggregate memos above but read from
  // the LEAN summary arrow. They drive a self-contained, read-only top-panel
  // render that paints BEFORE the full VT lands; once it does (`fullReady`),
  // the existing full-VT path takes over with no behavior change. We do NOT
  // touch the shared periodColumns / pastPeriodSet memos — the bottom panel and
  // edit path depend on those.
  const summaryPeriodColumns = useMemo(() => {
    // Both summary VTs (FR + sku) roll the SAME base → identical weekly period
    // columns. Derive from whichever has LANDED (prefer the sku summary) so
    // neither table's paint waits on the other: with a single shared axis keyed
    // only off the FR summary, a slow FR-summary cold start (~8s observed on
    // greg) blocked the bottom table even though the sku summary was ready in
    // ~2.7s (CHO-678 Phase C). Same-column detection the full path uses.
    const sku = skuSummaryVt.arrowTable;
    if (sku && sku.numRows > 0) return computePeriodColumns(skuSummaryVt.columns);
    const fr = summaryVt.arrowTable;
    if (fr && fr.numRows > 0) return computePeriodColumns(summaryVt.columns);
    return [];
  }, [skuSummaryVt.arrowTable, skuSummaryVt.columns, summaryVt.arrowTable, summaryVt.columns]);

  // Past/actuals period set from the summary's is_actuals measure rows: a period
  // is "past" if any FR's is_actuals value there is > 0 (max-aggregated server
  // side, mirroring the full path's union semantics).
  const summaryPastPeriodSet = useMemo(() => {
    const set = new Set<string>();
    // Read is_actuals from whichever summary established the axis (prefer sku),
    // so the past/actuals shading isn't gated on the slower summary either.
    const table =
      skuSummaryVt.arrowTable && skuSummaryVt.arrowTable.numRows > 0
        ? skuSummaryVt.arrowTable
        : summaryVt.arrowTable;
    if (!table || table.numRows === 0) {
      for (const p of summaryPeriodColumns) {
        if (isPastOrCurrentPeriod(p, "Week")) set.add(p);
      }
      return set;
    }
    const measureCol = table.getChild("measure");
    if (!measureCol) return set;
    const periodCols = summaryPeriodColumns.map((p) => ({ name: p, col: table.getChild(p) }));
    for (let i = 0; i < table.numRows; i++) {
      if (String(measureCol.get(i)) !== "is_actuals") continue;
      for (const { name, col } of periodCols) {
        if (set.has(name)) continue;
        if (col && Number(col.get(i)) > 0) set.add(name);
      }
      if (set.size === summaryPeriodColumns.length) break;
    }
    return set;
  }, [skuSummaryVt.arrowTable, summaryVt.arrowTable, summaryPeriodColumns]);

  // FirstReceiverSummary[] derived from the summary arrow: pick the row whose
  // `measure === topAggregateBy` per FR and read its period-column values in
  // summaryPeriodColumns order. topAggregateBy is a dep so switching the top
  // measure re-derives client-side instantly (no refetch — the summary carries
  // every measure as a row). `targetWOS` is real (server emits the per-FR
  // sales-weighted target → WOS heatmap), matching the FE's own rollup so cell
  // colors don't flip on backfill. `status`/`statusUpdatedAt` carry the persisted
  // Mark-Ready state (server reads current_status.json) so ready dots + dates
  // show in collapsed/lazy mode; live session edits overlay "edited" client-side
  // (see firstReceiverStatuses). `skuCount` is unused in rendering.
  const summaryFirstReceiverSummaries = useMemo<FirstReceiverSummary[]>(() => {
    const table = summaryVt.arrowTable;
    if (!table || table.numRows === 0 || summaryPeriodColumns.length === 0) return [];
    const frCol = table.getChild("first_receiver");
    const measureCol = table.getChild("measure");
    if (!frCol || !measureCol) return [];
    const twosCol = table.getChild("target_weeks_of_supply");
    const statusCol = table.getChild("status");
    const statusAtCol = table.getChild("status_updated_at");
    const periodCols = summaryPeriodColumns.map((p) => table.getChild(p));
    const out: FirstReceiverSummary[] = [];
    for (let i = 0; i < table.numRows; i++) {
      if (String(measureCol.get(i)) !== params.topAggregateBy) continue;
      const aggregateValues = periodCols.map((col) => {
        const v = col?.get(i);
        return v == null ? null : Number(v);
      });
      // Match skuRowMaterialize: treat non-positive / null target as "no target".
      const twosRaw = twosCol?.get(i);
      const targetWOS = twosRaw != null && Number(twosRaw) > 0 ? Number(twosRaw) : null;
      const statusStr = statusCol != null ? String(statusCol.get(i) ?? "baseline") : "baseline";
      const status: RowStatus =
        statusStr === "ready" || statusStr === "edited" ? statusStr : "baseline";
      const suaRaw = statusAtCol?.get(i);
      const statusUpdatedAt = suaRaw != null && String(suaRaw).length > 0 ? String(suaRaw) : null;
      out.push({
        firstReceiver: String(frCol.get(i)),
        skuCount: 0,
        aggregateValues,
        targetWOS,
        status,
        statusUpdatedAt,
      });
    }
    return out.sort((a, b) => a.firstReceiver.localeCompare(b.firstReceiver));
  }, [summaryVt.arrowTable, summaryPeriodColumns, params.topAggregateBy]);

  // Collapsed bottom-panel rows from the SKU-summary VT: one row per sku, picking
  // the row whose `measure === bottomAggregateBy` and reading its period values
  // (same week columns as the FR summary — both roll the same base). targetWOS is
  // the per-sku WOS-heatmap threshold (server-computed, matches the full path).
  // These are the read-only "SKU header" rows shown until the full VT backfills
  // the expandable PL/measure-walk detail (CHO-678 OQ3 § two-tier).
  const summarySkuRows = useMemo<SummarySkuRow[]>(() => {
    const table = skuSummaryVt.arrowTable;
    if (!table || table.numRows === 0 || summaryPeriodColumns.length === 0) return [];
    const skuColArrow = table.getChild("sku");
    const measureCol = table.getChild("measure");
    if (!skuColArrow || !measureCol) return [];
    const twosCol = table.getChild("target_weeks_of_supply");
    const plCountCol = table.getChild("pl_count");
    const hasInvCol = table.getChild("_has_inventory_data");
    const hasFcstCol = table.getChild("_has_sales_forecast");
    const periodCols = summaryPeriodColumns.map((p) => table.getChild(p));
    const out: SummarySkuRow[] = [];
    for (let i = 0; i < table.numRows; i++) {
      if (String(measureCol.get(i)) !== params.bottomAggregateBy) continue;
      const aggregateValues = periodCols.map((col) => {
        const v = col?.get(i);
        return v == null ? null : Number(v);
      });
      const twosRaw = twosCol?.get(i);
      const targetWOS = twosRaw != null && Number(twosRaw) > 0 ? Number(twosRaw) : null;
      const plCountRaw = plCountCol?.get(i);
      // Default missing flags to "present" (1) so nothing is hidden on legacy VTs.
      const hasInvRaw = hasInvCol?.get(i);
      const hasFcstRaw = hasFcstCol?.get(i);
      out.push({
        sku: String(skuColArrow.get(i)),
        aggregateValues,
        targetWOS,
        plCount: plCountRaw != null ? Number(plCountRaw) : 1,
        hasInventoryData: hasInvRaw == null || Number(hasInvRaw) > 0,
        hasSalesForecast: hasFcstRaw == null || Number(hasFcstRaw) > 0,
      });
    }
    return out.sort((a, b) => a.sku.localeCompare(b.sku));
  }, [skuSummaryVt.arrowTable, summaryPeriodColumns, params.bottomAggregateBy]);

  // Visible summary period columns honor the same Actuals collapse toggle.
  const summaryVisiblePeriodColumns = useMemo(
    () => (actualsExpanded ? summaryPeriodColumns : summaryPeriodColumns.filter((c) => !summaryPastPeriodSet.has(c))),
    [summaryPeriodColumns, summaryPastPeriodSet, actualsExpanded],
  );

  // Header-group builder. Defaults close over the full-VT period set, but
  // accepts an explicit (periods, past, visible) triple so the summary-mode
  // top panel can build its groups from the summary-derived columns without a
  // duplicate block (CHO-678 OQ3 Variant 2). The full-ready path passes
  // nothing and behaves exactly as before.
  const buildHeaderGroups = useCallback((
    frozenColSpan: number,
    opts?: { periods: string[]; past: Set<string>; visible: string[] },
  ): HeaderGroup[] => {
    const periodCols = opts?.periods ?? periodColumns;
    const pastSet = opts?.past ?? pastPeriodSet;
    const visibleCols = opts?.visible ?? visiblePeriodColumns;
    const hasAct = periodCols.some((c) => pastSet.has(c));
    const actualsCount = actualsExpanded ? periodCols.filter((c) => pastSet.has(c)).length : 0;
    const forecastCount = visibleCols.length - actualsCount;
    const headerGroupBase = css`font-size: 0.6875rem; font-weight: 500; color: ${theme.colors.mutedForeground};`;
    const frozenLabel = hasAct ? (
      <span
        css={css`
          cursor: pointer; user-select: none; display: inline-flex; align-items: center;
          gap: 2px; font-size: 10px; color: ${theme.colors.mutedForeground};
          padding: 2px 8px; border-radius: 3px; border: 1px solid ${theme.colors.border};
          background: ${theme.colors.pastPeriodBg}; float: right;
          &:hover { background: ${theme.colors.muted}; }
        `}
        onClick={() => setActualsExpanded((prev) => !prev)}
      >
        {actualsExpanded ? <ChevronLeft size={10} /> : <ChevronRight size={10} />} Actuals
      </span>
    ) : "";
    const groups: HeaderGroup[] = [
      {
        label: frozenLabel, colSpan: frozenColSpan,
        css: css`position: sticky; left: 0; z-index: 3; background: ${theme.colors.muted}; box-shadow: 2px 0 4px -2px rgba(0,0,0,0.08);`,
      },
    ];
    if (actualsExpanded && actualsCount > 0) {
      groups.push({
        label: "Actuals",
        colSpan: actualsCount,
        css: css`${headerGroupBase}; text-align: right; padding-right: 0.75rem; border-right: 2px solid ${theme.colors.border}; background-color: ${theme.colors.pastPeriodBg};`,
      });
    }
    if (forecastCount > 0) {
      groups.push({ label: "Forecast", colSpan: forecastCount, css: css`${headerGroupBase}; text-align: left; padding-left: 0.75rem;` });
    }
    groups.push({ label: "", colSpan: 1 }); // total/avg column
    return groups;
  }, [periodColumns, pastPeriodSet, visiblePeriodColumns, actualsExpanded]);

  // ── Parse Arrow table into SkuRow[] ───────────────────────────────────────

  // CHO-678 Phase 7 Step 2: the Arrow→SkuRow[] materialization (~0.9 s, §7H — the
  // only non-trivial client cost left) is now done INSIDE the parquet worker, in
  // the same pass it parses this VT's table (see `postProcess` on the vt hook +
  // skuRowMaterialize.runSkuRowsPostProcess). The worker returns ready rows
  // alongside the table, so the main thread does NO ~0.9 s reduce — it just reads
  // the precomputed result here. Synchronous again (no async state / skeleton):
  // `vt.postProcessResult` arrives together with `vt.arrowTable`.
  //
  // Fallback: if the worker path didn't produce a result (sync-parse fallback in
  // parquet.ts, which deliberately skips view-specific post-processing), we
  // materialize on the main thread via the SAME `materializeSkuRows` — correct,
  // just blocking, on that rare path.
  const allSkuRows = useMemo<SkuRow[]>(() => {
    const fromWorker = vt.postProcessResult as SkuRowsPostProcessResult | null | undefined;
    if (fromWorker && fromWorker.skuRows) return fromWorker.skuRows;
    // Worker path produced no result (sync-parse fallback in parquet.ts, which
    // skips view-specific post-processing) — materialize on the main thread via
    // the SAME logic. Correct, just blocking, on that rare path.
    const table = vt.arrowTable;
    if (!table || table.numRows === 0 || periodColumns.length === 0) return [];
    return materializeSkuRows(table, { periodColumns, skuCol, skuGrain: params.skuGrain });
    // Same dep rule as the original memo: recompute on a new table / period set.
    // skuCol / params.skuGrain are read fresh (a grain change arrives with a new
    // table). The worker result already encodes the correct grain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vt.postProcessResult, vt.arrowTable, periodColumns]);

  // The full VT has landed AND materialized into SkuRow[] — the moment the
  // existing full-fidelity top panel (edits/sort/select/drilldown) can take
  // over from the summary-mode render (CHO-678 OQ3 Variant 2).
  const fullReady = allSkuRows.length > 0;

  // CHO-678 OQ3 Phase C: SkuRow[] for the lazily-expanded SKUs only (detailVt).
  // Same materialization as allSkuRows; period columns come from the detail
  // table itself (the full VT's periodColumns may not be ready in collapsed mode).
  const detailSkuRows = useMemo<SkuRow[]>(() => {
    const fromWorker = detailVt.postProcessResult as SkuRowsPostProcessResult | null | undefined;
    if (fromWorker && fromWorker.skuRows) return fromWorker.skuRows;
    const table = detailVt.arrowTable;
    if (!table || table.numRows === 0) return [];
    // Materialize on the SAME period axis the bottom table renders on (the
    // summary axis pre-fullReady), so header and lazily-loaded PL cells align.
    const dCols = summaryPeriodColumns.length ? summaryPeriodColumns : computePeriodColumns(detailVt.columns);
    if (dCols.length === 0) return [];
    return materializeSkuRows(table, { periodColumns: dCols, skuCol, skuGrain: params.skuGrain });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailVt.postProcessResult, detailVt.arrowTable, detailVt.columns, summaryPeriodColumns]);

  // ── Unified period axis for the interactive content (CHO-678 OQ3 Phase C) ──
  // Both tables' interactive render keys on a period axis. It comes from the full
  // VT once ready, else from the summary VTs (collapsed-primary / lazyDetail). The
  // interactive content branch only renders when fullReady (non-lazy) OR lazyDetail
  // — in both cases this equals the right axis for top AND bottom, so routing both
  // through it keeps the full path byte-unchanged while making lazyDetail work.
  const activePeriodColumns = fullReady ? periodColumns : summaryPeriodColumns;
  const activeVisiblePeriodColumns = fullReady ? visiblePeriodColumns : summaryVisiblePeriodColumns;
  const activePastPeriodSet = fullReady ? pastPeriodSet : summaryPastPeriodSet;
  const activeVisiblePeriodSet = useMemo(
    () => (fullReady ? visiblePeriodSet : new Set(summaryVisiblePeriodColumns)),
    [fullReady, visiblePeriodSet, summaryVisiblePeriodColumns],
  );
  // Bottom-table column header groups on the unified axis (so the Actuals/
  // Forecast group spans match whichever source is driving the table).
  const skuStatusHeaderGroups = useMemo<HeaderGroup[]>(
    () => buildHeaderGroups(4, {
      periods: activePeriodColumns,
      past: activePastPeriodSet,
      visible: activeVisiblePeriodColumns,
    }),
    [buildHeaderGroups, activePeriodColumns, activePastPeriodSet, activeVisiblePeriodColumns],
  );
  // Top-table header groups on the same active axis (so the top renders in
  // lazyDetail mode, where the full periodColumns are empty).
  const statusHeaderGroups = useMemo<HeaderGroup[]>(
    () => buildHeaderGroups(4, {
      periods: activePeriodColumns,
      past: activePastPeriodSet,
      visible: activeVisiblePeriodColumns,
    }),
    [buildHeaderGroups, activePeriodColumns, activePastPeriodSet, activeVisiblePeriodColumns],
  );

  // PERF: first-paint mark — fires when the summary-mode top panel first becomes
  // renderable (summary arrow + its derived period schema ready) while the full
  // VT is still backfilling. Distinct from `retailer-supply-interactive` (which
  // marks full-ready). Logged once.
  const topPaintLoggedRef = useRef(false);
  useEffect(() => {
    if (topPaintLoggedRef.current) return;
    if (!summaryVt.arrowTable || summaryPeriodColumns.length === 0) return;
    if (summaryFirstReceiverSummaries.length === 0) return;
    topPaintLoggedRef.current = true;
    performance.mark("retailer-supply-top-paint");
    let topPaintMs: number | null = null;
    try {
      performance.measure("retailer-supply-time-to-top-paint", "app-start", "retailer-supply-top-paint");
      const m = performance.getEntriesByName("retailer-supply-time-to-top-paint")[0];
      if (m) topPaintMs = Math.round(m.duration);
    } catch {
      // app-start may be missing in tests / HMR.
    }
    console.log("[PERF_SUMMARY] [FE] [RetailerSupplyView] topPaint", {
      topPaintMs,
      summaryRows: summaryVt.arrowTable.numRows,
      summaryFirstReceivers: summaryFirstReceiverSummaries.length,
      summaryPeriods: summaryPeriodColumns.length,
      fullReadyAtTopPaint: fullReady,
    });
  }, [summaryVt.arrowTable, summaryPeriodColumns, summaryFirstReceiverSummaries, fullReady]);

  // PERF: bottom collapsed-paint mark — fires when the SKU-summary rows first
  // become renderable while the full VT is still backfilling. With the two-tier
  // collapsed load BOTH tables are usable at this point (CHO-678 OQ3 § two-tier).
  const bottomPaintLoggedRef = useRef(false);
  useEffect(() => {
    if (bottomPaintLoggedRef.current) return;
    if (summarySkuRows.length === 0) return;
    bottomPaintLoggedRef.current = true;
    performance.mark("retailer-supply-bottom-paint");
    let bottomPaintMs: number | null = null;
    try {
      performance.measure("retailer-supply-time-to-bottom-paint", "app-start", "retailer-supply-bottom-paint");
      const m = performance.getEntriesByName("retailer-supply-time-to-bottom-paint")[0];
      if (m) bottomPaintMs = Math.round(m.duration);
    } catch {
      // app-start may be missing in tests / HMR.
    }
    console.log("[PERF_SUMMARY] [FE] [RetailerSupplyView] bottomPaint", {
      bottomPaintMs,
      summarySkus: summarySkuRows.length,
      fullReadyAtBottomPaint: fullReady,
    });
  }, [summarySkuRows, fullReady]);

  // PERF: mark when each of the three cold-load streams finishes for the
  // first time, then fire `retailer-supply-interactive` once all three are
  // ready. The summary log identifies the long pole so we can compare
  // against the network HAR waterfall — HAR shows request times, this
  // shows the cross-stream convergence the HAR can't easily express.
  const streamReadyAtRef = useRef<{
    mainVt?: number;
    schema?: number;
    exceptionTypes?: number;
  }>({});
  const interactiveLoggedRef = useRef(false);
  useEffect(() => {
    // The main-VT stream is "ready" when the Arrow table lands. Its SkuRow[]
    // materialization is done in the parquet worker BEFORE the table is handed
    // to the main thread (CHO-678 Phase 7 Step 2), so `vt.arrowTable` being set
    // already implies the derived rows are available — no extra gate needed.
    if (vt.arrowTable && streamReadyAtRef.current.mainVt === undefined) {
      streamReadyAtRef.current.mainVt = performance.now();
    }
    if (
      schema.columnProfiles.length > 0 &&
      streamReadyAtRef.current.schema === undefined
    ) {
      streamReadyAtRef.current.schema = performance.now();
    }
    if (
      exceptionTypes.length > 0 &&
      streamReadyAtRef.current.exceptionTypes === undefined
    ) {
      streamReadyAtRef.current.exceptionTypes = performance.now();
    }
    if (interactiveLoggedRef.current) return;
    const r = streamReadyAtRef.current;
    if (
      r.mainVt === undefined ||
      r.schema === undefined ||
      r.exceptionTypes === undefined
    )
      return;
    interactiveLoggedRef.current = true;
    performance.mark("retailer-supply-interactive");
    let timeToInteractiveMs: number | null = null;
    try {
      performance.measure(
        "retailer-supply-time-to-interactive",
        "app-start",
        "retailer-supply-interactive",
      );
      const measure = performance.getEntriesByName(
        "retailer-supply-time-to-interactive",
      )[0];
      if (measure) timeToInteractiveMs = Math.round(measure.duration);
    } catch {
      // app-start may be missing in tests / HMR — fall back to performance.now.
    }
    const longPole =
      r.mainVt >= r.schema && r.mainVt >= r.exceptionTypes
        ? "mainVt"
        : r.schema >= r.exceptionTypes
          ? "schema"
          : "exceptionTypes";
    console.log("[PERF_SUMMARY] [FE] [RetailerSupplyView]", {
      timeToInteractiveMs,
      mainVtReadyMs: Math.round(r.mainVt),
      schemaReadyMs: Math.round(r.schema),
      exceptionTypesReadyMs: Math.round(r.exceptionTypes),
      longPole,
      mainVtRows: vt.arrowTable?.numRows ?? 0,
      mainVtColumns: vt.columns.length,
    });
  }, [vt.arrowTable, vt.columns, schema.columnProfiles, exceptionTypes]);

  // Merge the lazily-fetched detail rows in alongside the full rows so the edit /
  // save / parity / dirty memos resolve rows loaded on-expand (CHO-678 Phase C).
  // Detail rows win on id collision (they're the fresher fetch, e.g. post-save).
  // Forward-compatible: once the upfront full load is removed (Stage 3),
  // allSkuRows is empty and these maps are purely the detail set.
  const originalMeasuresMap = useMemo(() => {
    const map = new Map<string, Record<string, number[]>>();
    for (const row of allSkuRows) map.set(row.id, row.measures);
    for (const row of detailSkuRows) map.set(row.id, row.measures);
    return map;
  }, [allSkuRows, detailSkuRows]);

  const skuRowById = useMemo(() => {
    const map = new Map<string, SkuRow>();
    for (const row of allSkuRows) map.set(row.id, row);
    for (const row of detailSkuRows) map.set(row.id, row);
    return map;
  }, [allSkuRows, detailSkuRows]);

  // Client-side filters that don't pass through to the VT query: derived
  // per-row from the loaded data. Predicate-style — empty selection (or both
  // values selected) is a pass-through. Top-panel aggregation and the bottom
  // table both consume the filtered set so the two panels stay in sync.
  const filteredSkuRows = useMemo<SkuRow[]>(() => {
    const inv = params.filterHasInventoryData;
    const fcst = params.filterHasSalesForecast;
    const invActive = inv.length > 0 && inv.length < BOOLEAN_FILTER_OPTIONS.length;
    const fcstActive = fcst.length > 0 && fcst.length < BOOLEAN_FILTER_OPTIONS.length;
    if (!invActive && !fcstActive) return allSkuRows;
    return allSkuRows.filter((row) => {
      if (invActive) {
        const has = row.iohSource !== "netsuite_fallback";
        if (!inv.includes(has ? "Yes" : "No")) return false;
      }
      if (fcstActive) {
        const sales = row.measures.forecast_sales ?? [];
        const isAct = row.measures.is_actuals ?? [];
        let hasAny = false;
        for (let i = 0; i < sales.length; i++) {
          if (isAct[i]) continue;
          if (Math.abs(sales[i]) > 1e-9) { hasAny = true; break; }
        }
        if (!fcst.includes(hasAny ? "Yes" : "No")) return false;
      }
      return true;
    });
  }, [allSkuRows, params.filterHasInventoryData, params.filterHasSalesForecast]);

  // Derive the parity-run suggestion fresh from the current baseline and
  // override on every relevant change. This is the single source of truth
  // the chip renders against — no snapshotted cells in state — so a VT
  // refetch that replaces the engine baseline immediately recomputes the
  // ghost values, and the row-vanishing case (refetch removes the row)
  // collapses to no chip rather than a chip pinned to a missing row.
  const parityHint = useMemo<{
    rowId: string;
    editedWeekIdx: number;
    cells: ParityRunCell[];
  } | null>(() => {
    if (!parityHintAnchor) return null;
    const row = skuRowById.get(parityHintAnchor.rowId);
    if (!row) return null;
    const baseline = row.measures.simulated_shipments_placed_atomic;
    if (!baseline) return null;
    const savedOverride = row.measures.simulated_shipments_placed_planner_override ?? [];
    const override =
      editedMeasures.get(parityHintAnchor.rowId)?.simulated_shipments_placed_planner_override
      ?? savedOverride;
    const effectiveSaved = effectiveSavedForParity(
      savedOverride,
      baseline,
      clearedCells.get(parityHintAnchor.rowId),
    );
    const cells = suggestParityRun(baseline, override, effectiveSaved, parityHintAnchor.editedWeekIdx);
    if (cells.length === 0) return null;
    return {
      rowId: parityHintAnchor.rowId,
      editedWeekIdx: parityHintAnchor.editedWeekIdx,
      cells,
    };
  }, [parityHintAnchor, editedMeasures, clearedCells, skuRowById]);

  const byFirstReceiver = useMemo(() => {
    const m = new Map<string, SkuRow[]>();
    for (const row of filteredSkuRows) {
      if (!m.has(row.firstReceiver)) m.set(row.firstReceiver, []);
      m.get(row.firstReceiver)!.push(row);
    }
    return m;
  }, [filteredSkuRows]);

  // ── Apply edits ───────────────────────────────────────────────────────────
  //
  // Local recalcs never iterate every row. Edits live in `editedMeasures`
  // (keyed by row id) and get overlaid lazily at the points of use — the sort
  // comparator, the cell renderer, and the FR aggregator below — so a keystroke
  // only triggers compute for the one row whose measures changed (and the one
  // FR group it rolls up into).

  // Set of first-receivers with at least one row touched this session. Drives
  // the cache-skip for the two group memos below: clean groups reuse their
  // prior entry instead of re-walking SKUs × periods on every keystroke.
  const dirtyFirstReceivers = useMemo(() => {
    const set = new Set<string>();
    for (const id of editedMeasures.keys()) {
      const fr = skuRowById.get(id)?.firstReceiver;
      if (fr) set.add(fr);
    }
    for (const id of clearedCells.keys()) {
      const fr = skuRowById.get(id)?.firstReceiver;
      if (fr) set.add(fr);
    }
    return set;
  }, [editedMeasures, clearedCells, skuRowById]);

  /** Top-panel aggregate by first receiver. Reflects in-session edits live:
   *  for each dirty FR we re-walk its SKUs through `editedMeasures` so the
   *  group row's totals (and the delta column in particular) move in lockstep
   *  with the cells the planner is typing. Clean FRs reuse their cached
   *  summary so a single keystroke recomputes one FR's SKUs × periods, not
   *  the whole table. Cache invalidates when the underlying `byFirstReceiver`
   *  identity or the chosen aggregate changes (data refetch / aggregate
   *  picker). */
  const summariesCacheRef = useRef<{
    source: typeof byFirstReceiver;
    aggregateBy: string;
    map: Map<string, FirstReceiverSummary>;
  } | null>(null);
  const allFirstReceiverSummaries = useMemo(() => {
    // CHO-678 Phase C: with the full VT severed (lazyDetail), the top table is
    // driven directly by the FR-summary VT (status falls back to baseline — real
    // saved-edit status is the deferred Stage-4 follow-up). The dependent
    // status/diff memos are naturally empty here (byFirstReceiver is empty), so
    // renderFirstReceiverCells falls back to row.status / no shipment-diff shading.
    if (lazyDetail) return summaryFirstReceiverSummaries;
    const cached =
      summariesCacheRef.current?.source === byFirstReceiver &&
      summariesCacheRef.current?.aggregateBy === params.topAggregateBy
        ? summariesCacheRef.current.map
        : null;
    const out = new Map<string, FirstReceiverSummary>();
    const getMeasures = (row: SkuRow) => editedMeasures.get(row.id) ?? row.measures;
    for (const [firstReceiver, skus] of byFirstReceiver) {
      if (cached && !dirtyFirstReceivers.has(firstReceiver)) {
        const prev = cached.get(firstReceiver);
        if (prev !== undefined) {
          out.set(firstReceiver, prev);
          continue;
        }
      }
      out.set(
        firstReceiver,
        aggregateOneFirstReceiver(
          firstReceiver,
          skus,
          periodColumns.length,
          params.topAggregateBy,
          getMeasures,
        ),
      );
    }
    summariesCacheRef.current = {
      source: byFirstReceiver,
      aggregateBy: params.topAggregateBy,
      map: out,
    };
    return [...out.values()].sort((a, b) => a.firstReceiver.localeCompare(b.firstReceiver));
  }, [lazyDetail, summaryFirstReceiverSummaries, byFirstReceiver, dirtyFirstReceivers, editedMeasures, periodColumns.length, params.topAggregateBy]);

  /** Status applies to the full first-receiver group, not per-SKU. Derive from
   *  the group's SKUs + local state: any diff-from-engine in an editable
   *  (non-past) period → edited; else any queued clear → baseline; else
   *  fall back to the server-denormalised row.status (uniform across group).
   *  Past-period cells aren't individually clearable (no input/X button), so
   *  stale historical saved edits there shouldn't block a group from returning
   *  to baseline when the user clears everything they can actually see/edit.
   *
   *  Incremental: clean groups (no edits/clears under them) reuse their prior
   *  status — only dirty groups get re-walked. The cache is invalidated when
   *  byFirstReceiver identity changes (i.e. on data refetch). */
  const statusesCacheRef = useRef<{ source: typeof byFirstReceiver; map: Map<string, RowStatus> } | null>(null);
  const firstReceiverStatuses = useMemo(() => {
    // Lazy mode: the full per-SKU rows aren't loaded, so derive a live "edited"
    // overlay from the in-memory (expanded/detail) edits keyed by FR; "ready"/
    // baseline come from the summary's server status (row.status, ready wins).
    // (Saved-but-unexpanded edits aren't reflected here — that needs the deferred
    // saved-edit summary handling; the summary aggregates are engine-only too.)
    if (lazyDetail) {
      const out = new Map<string, RowStatus>();
      const readyFRs = new Set(
        summaryFirstReceiverSummaries.filter((s) => s.status === "ready").map((s) => s.firstReceiver),
      );
      const editedRowIds = new Set<string>([...editedMeasures.keys(), ...clearedCells.keys()]);
      for (const id of editedRowIds) {
        const fr = skuRowById.get(id)?.firstReceiver;
        if (fr && !readyFRs.has(fr)) out.set(fr, "edited");
      }
      return out;
    }
    const cached = statusesCacheRef.current?.source === byFirstReceiver
      ? statusesCacheRef.current.map
      : null;
    const pastIdxSet = new Set<number>();
    periodColumns.forEach((p, i) => { if (pastPeriodSet.has(p)) pastIdxSet.add(i); });
    const out = new Map<string, RowStatus>();
    for (const [firstReceiver, skus] of byFirstReceiver) {
      if (cached && !dirtyFirstReceivers.has(firstReceiver)) {
        const prev = cached.get(firstReceiver);
        if (prev !== undefined) {
          out.set(firstReceiver, prev);
          continue;
        }
      }
      let hasDiff = false;
      let hasClears = false;
      for (const row of skus) {
        const eff = editedMeasures.get(row.id) ?? row.measures;
        const atomic = row.measures.simulated_shipments_placed_atomic ?? [];
        const override = eff.simulated_shipments_placed_planner_override ?? [];
        const savedOverride = row.measures.simulated_shipments_placed_planner_override ?? [];
        const cleared = clearedCells.get(row.id);
        if ((cleared?.size ?? 0) > 0) hasClears = true;
        const n = Math.max(atomic.length, override.length);
        for (let i = 0; i < n; i++) {
          if (pastIdxSet.has(i)) continue;
          if (cleared?.has(i)) continue;
          if (atomic[i] === undefined) continue;
          // Only user intent flips the status: a pending session edit, or a
          // user-source saved edit. Engine-source pins (which carry no
          // edited_at — transform suppresses it) shouldn't register as
          // "edited" even if atomic has since drifted from the pinned value.
          const isSessionEdit = override[i] !== savedOverride[i];
          const isUserSavedEdit = row.editedAt[i] != null;
          if (isSessionEdit || isUserSavedEdit) { hasDiff = true; break; }
        }
        if (hasDiff) break;
      }
      const serverStatus = skus[0]?.status ?? "baseline";
      // Server "ready" means the user has explicitly approved the saved state;
      // it beats the local hasDiff check (which would otherwise always flag
      // rows with saved overrides as "edited").
      if (serverStatus === "ready") {
        out.set(firstReceiver, serverStatus);
      } else {
        out.set(firstReceiver, hasDiff ? "edited" : hasClears ? "baseline" : serverStatus);
      }
    }
    statusesCacheRef.current = { source: byFirstReceiver, map: out };
    return out;
  }, [lazyDetail, summaryFirstReceiverSummaries, skuRowById, byFirstReceiver, dirtyFirstReceivers, editedMeasures, clearedCells, periodColumns, pastPeriodSet]);

  /** Per-period diff indicator for each first-receiver: period idx is in the
   *  set if any SKU under this first-receiver has atomic ≠ override at that
   *  period. Drives the darker cell background on the collapsed summary row
   *  when the top aggregate is "Forecast Shipments (baseline)" — mirrors the
   *  per-cell bg in the expanded detail's baseline row.
   *
   *  Incremental in the same way as firstReceiverStatuses: clean groups reuse
   *  their prior Set instance, so a downstream React.memo can compare by
   *  reference and skip re-render. */
  const diffPeriodsCacheRef = useRef<{ source: typeof byFirstReceiver; map: Map<string, Set<number>> } | null>(null);
  const firstReceiverDiffPeriods = useMemo(() => {
    const cached = diffPeriodsCacheRef.current?.source === byFirstReceiver
      ? diffPeriodsCacheRef.current.map
      : null;
    const out = new Map<string, Set<number>>();
    for (const [firstReceiver, skus] of byFirstReceiver) {
      if (cached && !dirtyFirstReceivers.has(firstReceiver)) {
        const prev = cached.get(firstReceiver);
        if (prev !== undefined) out.set(firstReceiver, prev);
        // Clean group with no prior entry = no diffs; omit from map (matches
        // legacy behavior — only groups WITH diffs appear).
        continue;
      }
      let set: Set<number> | null = null;
      for (const row of skus) {
        const eff = editedMeasures.get(row.id) ?? row.measures;
        const atomic = row.measures.simulated_shipments_placed_atomic ?? [];
        const override = eff.simulated_shipments_placed_planner_override ?? [];
        const cleared = clearedCells.get(row.id);
        const n = Math.max(atomic.length, override.length);
        for (let i = 0; i < n; i++) {
          if (cleared?.has(i)) continue;
          if (atomic[i] === undefined) continue;
          if (sameRounded(atomic[i], override[i])) continue;
          if (!set) set = new Set<number>();
          set.add(i);
        }
      }
      if (set) out.set(firstReceiver, set);
    }
    diffPeriodsCacheRef.current = { source: byFirstReceiver, map: out };
    return out;
  }, [byFirstReceiver, dirtyFirstReceivers, editedMeasures, clearedCells]);

  const visibleFirstReceiverSummaries = useMemo(() => {
    // Pure-fallback FRs (no inventory anchor) have an all-null aggregate on the
    // suppressed measures (WOS / inventory). We DON'T hide them here on that
    // basis: they're already kept out of the default view by the "Has Inventory
    // Data" filter (filteredSkuRows), and when the planner relaxes that filter
    // we want them shown with "—" for the suppressed measure rather than
    // silently dropped. The explicit filter is the single visibility control.
    if (!selectedFirstReceiver) return allFirstReceiverSummaries;
    return allFirstReceiverSummaries.filter((r) => r.firstReceiver === selectedFirstReceiver);
  }, [allFirstReceiverSummaries, selectedFirstReceiver]);

  const handleTopSort = useCallback(
    (sortKey: string, multi: boolean) => setTopSort((prev) => getNextSortState(prev, sortKey, multi)),
    []
  );

  // Stable sort cache for the top panel. The aggregateValues used by the
  // sort comparator are derived from `editedMeasures`, so without this cache
  // every keystroke would re-sort and shuffle rows under the planner's
  // cursor. Cache hits reuse the prior order whenever only edits have
  // changed; misses (column click, VT data reload, aggregateBy/period
  // change, filter change) recompute fresh against the current aggregates.
  const sortedFirstReceiverIdsRef = useRef<{
    byFirstReceiver: typeof byFirstReceiver;
    sort: SortCriteria[];
    aggregateBy: string;
    periodColumns: typeof periodColumns;
    selectedFirstReceiver: string | null;
    ids: string[];
  } | null>(null);

  const sortedFirstReceiverSummaries = useMemo(() => {
    if (topSort.length === 0) return visibleFirstReceiverSummaries;

    const cache = sortedFirstReceiverIdsRef.current;
    const sortsEqual =
      cache != null &&
      cache.sort.length === topSort.length &&
      cache.sort.every(
        (s, i) => s.column === topSort[i].column && s.direction === topSort[i].direction,
      );
    const cacheValid =
      cache != null &&
      sortsEqual &&
      cache.byFirstReceiver === byFirstReceiver &&
      cache.aggregateBy === params.topAggregateBy &&
      cache.periodColumns === activePeriodColumns &&
      cache.selectedFirstReceiver === selectedFirstReceiver;

    if (cacheValid) {
      const byId = new Map(visibleFirstReceiverSummaries.map((r) => [r.firstReceiver, r]));
      const ordered: FirstReceiverSummary[] = [];
      for (const id of cache.ids) {
        const r = byId.get(id);
        if (r !== undefined) ordered.push(r);
      }
      if (ordered.length === visibleFirstReceiverSummaries.length) return ordered;
      // Fall through if the visible set diverged from the cached IDs (e.g.
      // a row appeared/disappeared) — recompute fresh.
    }

    const rows = [...visibleFirstReceiverSummaries];
    rows.sort((a, b) => {
      for (const { column, direction } of topSort) {
        const mult = direction === "desc" ? -1 : 1;
        if (column === "first_receiver") {
          const cmp = a.firstReceiver.localeCompare(b.firstReceiver);
          if (cmp !== 0) return cmp * mult;
        } else {
          const pi = activePeriodColumns.indexOf(column);
          if (pi >= 0) {
            const va = a.aggregateValues[pi] ?? 0;
            const vb = b.aggregateValues[pi] ?? 0;
            if (va !== vb) return (va - vb) * mult;
          }
        }
      }
      return 0;
    });

    sortedFirstReceiverIdsRef.current = {
      byFirstReceiver,
      sort: topSort,
      aggregateBy: params.topAggregateBy,
      periodColumns: activePeriodColumns,
      selectedFirstReceiver,
      ids: rows.map((r) => r.firstReceiver),
    };

    return rows;
  }, [
    visibleFirstReceiverSummaries,
    topSort,
    activePeriodColumns,
    byFirstReceiver,
    params.topAggregateBy,
    selectedFirstReceiver,
  ]);

  const visibleSkuRows = useMemo(() => {
    if (!selectedFirstReceiver) return filteredSkuRows;
    return filteredSkuRows.filter((r) => r.firstReceiver === selectedFirstReceiver);
  }, [filteredSkuRows, selectedFirstReceiver]);

  const handleSkuSort = useCallback(
    (sortKey: string, multi: boolean) => setSkuSort((prev) => getNextSortState(prev, sortKey, multi)),
    []
  );

  const sortedSkuRows = useMemo(() => {
    const rows = [...visibleSkuRows];
    // Group rows by SKU first, then planning_level within. Used as the
    // default ordering and as the final tiebreak under user sort so that
    // a multi-PL SKU's rows stay contiguous (which the SKU-header grouping
    // below relies on when it walks for the first row of each SKU group).
    const tiebreak = (a: SkuRow, b: SkuRow): number =>
      a.sku.localeCompare(b.sku) || a.planningLevel.localeCompare(b.planningLevel);
    if (skuSort.length === 0) {
      rows.sort(tiebreak);
      return rows;
    }
    const key = params.bottomAggregateBy;
    // Sort by saved (VT-loaded) measures only — never against the in-progress
    // edit overlay. Re-sorting on every keystroke would yank the row the
    // planner is editing out from under the cursor when the sort is by a
    // value column. Order refreshes naturally on save (VT refetch swaps
    // r.measures) and on explicit column-header clicks.
    rows.sort((a, b) => {
      for (const { column, direction } of skuSort) {
        const mult = direction === "desc" ? -1 : 1;
        let va: string | number;
        let vb: string | number;
        if (column === "planning_level") {
          va = a.planningLevel; vb = b.planningLevel;
        } else if (column === "sku") {
          va = a.sku; vb = b.sku;
        } else {
          // Period column — sort by the saved aggregate measure value.
          const pi = periodColumns.indexOf(column);
          if (pi >= 0) {
            va = a.measures[key]?.[pi] ?? 0;
            vb = b.measures[key]?.[pi] ?? 0;
          } else {
            va = 0; vb = 0;
          }
        }
        if (typeof va === "string" && typeof vb === "string") {
          const cmp = va.localeCompare(vb);
          if (cmp !== 0) return cmp * mult;
        } else {
          if (va !== vb) return ((va as number) - (vb as number)) * mult;
        }
      }
      return tiebreak(a, b);
    });
    return rows;
  }, [visibleSkuRows, skuSort, periodColumns, params.bottomAggregateBy]);

  // ── SKU-level grouping for the bottom panel's nested header rows ──────────
  //
  // The bottom panel now shows one SKU "header" row per SKU (always visible)
  // followed — when the SKU is expanded — by its planning_level rows (each
  // still owning the existing measure walk via DataTable's expansion). To
  // keep things lean we reuse `aggregateOneFirstReceiver` for the per-SKU
  // aggregate (its logic is generic over the group label).

  /** Ordered groups: one (sku, plRows[]) per SKU, in the order their first
   *  row appears in sortedSkuRows. Relies on sortedSkuRows' (sku, pl)
   *  tiebreak — same-SKU rows are always contiguous, so first-occurrence
   *  order is stable and complete. */
  const skuGroupsForBottom = useMemo(() => {
    const seen = new Map<string, SkuRow[]>();
    for (const r of sortedSkuRows) {
      if (!seen.has(r.sku)) seen.set(r.sku, []);
      seen.get(r.sku)!.push(r);
    }
    return [...seen.entries()].map(([sku, rows]) => ({ sku, rows }));
  }, [sortedSkuRows]);

  /** Per-SKU rolled-up summary for the "show" measure. Mirrors the FR cache
   *  pattern: clean SKUs reuse their prior summary, so a keystroke only
   *  triggers re-aggregation for the one SKU whose row changed. */
  const dirtySkus = useMemo(() => {
    const set = new Set<string>();
    for (const id of editedMeasures.keys()) {
      const sku = skuRowById.get(id)?.sku;
      if (sku) set.add(sku);
    }
    for (const id of clearedCells.keys()) {
      const sku = skuRowById.get(id)?.sku;
      if (sku) set.add(sku);
    }
    return set;
  }, [editedMeasures, clearedCells, skuRowById]);

  const skuSummariesCacheRef = useRef<{
    source: typeof skuGroupsForBottom;
    aggregateBy: string;
    map: Map<string, FirstReceiverSummary>;
  } | null>(null);
  const skuSummaries = useMemo(() => {
    const cached =
      skuSummariesCacheRef.current?.source === skuGroupsForBottom &&
      skuSummariesCacheRef.current?.aggregateBy === params.bottomAggregateBy
        ? skuSummariesCacheRef.current.map
        : null;
    const out = new Map<string, FirstReceiverSummary>();
    const getMeasures = (row: SkuRow) => editedMeasures.get(row.id) ?? row.measures;
    for (const { sku, rows } of skuGroupsForBottom) {
      if (cached && !dirtySkus.has(sku)) {
        const prev = cached.get(sku);
        if (prev !== undefined) {
          out.set(sku, prev);
          continue;
        }
      }
      out.set(
        sku,
        aggregateOneFirstReceiver(
          sku,
          rows,
          periodColumns.length,
          params.bottomAggregateBy,
          getMeasures,
        ),
      );
    }
    skuSummariesCacheRef.current = {
      source: skuGroupsForBottom,
      aggregateBy: params.bottomAggregateBy,
      map: out,
    };
    return out;
  }, [skuGroupsForBottom, dirtySkus, editedMeasures, periodColumns.length, params.bottomAggregateBy]);

  /** SKU-level status: edited if any PL is edited, else ready if all PLs are
   *  ready, else baseline. Reuses the FR-level status so saved-but-stale
   *  edits don't lock a group out of returning to baseline. */
  const skuHeaderStatuses = useMemo(() => {
    const out = new Map<string, RowStatus>();
    for (const { sku, rows } of skuGroupsForBottom) {
      let anyEdited = false;
      let allReady = rows.length > 0;
      for (const r of rows) {
        const st = firstReceiverStatuses.get(r.firstReceiver) ?? r.status;
        if (st === "edited") anyEdited = true;
        if (st !== "ready") allReady = false;
      }
      out.set(sku, anyEdited ? "edited" : allReady ? "ready" : "baseline");
    }
    return out;
  }, [skuGroupsForBottom, firstReceiverStatuses]);

  /** Discriminated union driving the bottom DataTable. Multi-PL SKUs emit a
   *  header row (always) plus their PL rows when expanded; single-PL SKUs
   *  collapse to a single standalone PL row (no header) since there's no
   *  hidden second state to surface. Each PL row keeps its existing
   *  DataTable-owned measure-walk expansion regardless of standalone vs
   *  nested, so the third level of the nesting needs no rework. */
  type BottomItem =
    | { kind: "header"; id: string; sku: string; summary: FirstReceiverSummary; status: RowStatus; plCount: number; isOpen: boolean; isSummary?: boolean }
    | { kind: "pl"; id: string; row: SkuRow; standalone: boolean };
  const bottomItems = useMemo<BottomItem[]>(() => {
    const primary = skuSort[0];

    // ── Summary-driven bottom (CHO-678 OQ3 Phase C) ───────────────────────
    // Before the full VT lands, the bottom table is built from the cheap
    // sku-summary VT: one collapsed "SKU header" per sku, and — for SKUs the
    // planner has expanded — the PL rows lazily fetched by detailVt. This is
    // what makes both tables usable immediately and fires a detail VT only on
    // expand. (When the full VT IS ready, the full client-side path below runs
    // unchanged.)
    if (!fullReady) {
      const detailBySku = new Map<string, SkuRow[]>();
      for (const r of detailSkuRows) {
        if (!detailBySku.has(r.sku)) detailBySku.set(r.sku, []);
        detailBySku.get(r.sku)!.push(r);
      }
      // Static filters applied to the COLLAPSED list via the server-emitted
      // per-sku flags (any-row rollups), so the bottom rows narrow without the
      // full per-PL rows. Same pass-through rule as filteredSkuRows.
      const inv = params.filterHasInventoryData;
      const fcst = params.filterHasSalesForecast;
      const invActive = inv.length > 0 && inv.length < BOOLEAN_FILTER_OPTIONS.length;
      const fcstActive = fcst.length > 0 && fcst.length < BOOLEAN_FILTER_OPTIONS.length;
      const passesFilters = (sr: SummarySkuRow) => {
        if (invActive && !inv.includes(sr.hasInventoryData ? "Yes" : "No")) return false;
        if (fcstActive && !fcst.includes(sr.hasSalesForecast ? "Yes" : "No")) return false;
        return true;
      };
      // Sort headers by the summary's value at the sorted period (summary-derived,
      // no full rows needed); default to the summary's locale sku order.
      const periodPi = primary ? summaryPeriodColumns.indexOf(primary.column) : -1;
      const filtered = summarySkuRows.filter(passesFilters);
      const ordered = periodPi >= 0
        ? [...filtered].sort((a, b) => {
            const va = a.aggregateValues[periodPi] ?? 0;
            const vb = b.aggregateValues[periodPi] ?? 0;
            const mult = primary!.direction === "desc" ? -1 : 1;
            if (va !== vb) return (va - vb) * mult;
            return a.sku.localeCompare(b.sku);
          })
        : filtered;
      const out: BottomItem[] = [];
      for (const sr of ordered) {
        const plRows = detailBySku.get(sr.sku) ?? [];
        const isOpen = expandedSummarySkus.has(sr.sku);
        out.push({
          kind: "header",
          id: `hdr:${sr.sku}`,
          sku: sr.sku,
          // SummarySkuRow → the FirstReceiverSummary shape renderSkuCells reads.
          summary: {
            firstReceiver: sr.sku, skuCount: 0,
            aggregateValues: sr.aggregateValues, targetWOS: sr.targetWOS,
            status: "baseline", statusUpdatedAt: null,
          },
          status: "baseline",
          // Server-emitted distinct-PL count → multi-PL badge. (Single-PL SKUs
          // still render as a collapsed header here; the eager-fetch-to-
          // standalone optimization is a deferred follow-up.)
          plCount: sr.plCount,
          isOpen,
          isSummary: true,
        });
        if (isOpen) {
          for (const r of plRows) out.push({ kind: "pl", id: r.id, row: r, standalone: false });
        }
      }
      return out;
    }

    // ── Full client-side bottom (unchanged) ───────────────────────────────
    // When the primary sort criterion is a period column, reorder SKU groups
    // by their summary's value at that period. Without this, groups land in
    // first-occurrence order from sortedSkuRows — which under a period sort
    // is the max (asc) or min (desc) of any underlying PL value, not the
    // SKU's aggregate. SKU/PL sorts are left as-is: same-SKU rows are
    // already contiguous in sortedSkuRows, so Map insertion order naturally
    // matches the SKU primary; PL-primary group ordering would need a
    // different rule and is intentionally out of scope.
    const periodPi = primary ? periodColumns.indexOf(primary.column) : -1;
    const orderedGroups = periodPi >= 0
      ? [...skuGroupsForBottom].sort((a, b) => {
          const va = skuSummaries.get(a.sku)?.aggregateValues[periodPi] ?? 0;
          const vb = skuSummaries.get(b.sku)?.aggregateValues[periodPi] ?? 0;
          const mult = primary!.direction === "desc" ? -1 : 1;
          if (va !== vb) return (va - vb) * mult;
          return a.sku.localeCompare(b.sku);
        })
      : skuGroupsForBottom;
    const out: BottomItem[] = [];
    for (const { sku, rows } of orderedGroups) {
      // Show SKU groups even when the chosen aggregate is all-null (pure-fallback
      // SKUs on a suppressed measure) — they render with "—" so the group still
      // shows under a visible fallback FR. Default view stays clean because the
      // "Has Inventory Data" filter keeps fallback rows out unless relaxed.
      // Still skip a sku with no computed summary at all (nothing to render).
      const summary = skuSummaries.get(sku);
      if (!summary) continue;

      // Single-PL SKUs render as a single standalone PL row (no header). The
      // header would just be a duplicate of the one row beneath it — the
      // aggregate is the row. Multi-PL keeps the header + nested rows pattern.
      if (rows.length === 1) {
        const r = rows[0];
        out.push({ kind: "pl", id: r.id, row: r, standalone: true });
        continue;
      }
      const isOpen = expandedSkus.has(sku);
      out.push({
        kind: "header",
        id: `hdr:${sku}`,
        sku,
        summary,
        status: skuHeaderStatuses.get(sku) ?? "baseline",
        plCount: rows.length,
        isOpen,
      });
      if (isOpen) {
        for (const r of rows) out.push({ kind: "pl", id: r.id, row: r, standalone: false });
      }
    }
    return out;
  }, [fullReady, detailSkuRows, summarySkuRows, expandedSummarySkus, summaryPeriodColumns, params.filterHasInventoryData, params.filterHasSalesForecast, skuGroupsForBottom, skuSummaries, skuHeaderStatuses, expandedSkus, skuSort, periodColumns]);

  // ── Retailer status ───────────────────────────────────────────────────────

  /** Count of distinct pending edits or clears across all retailers.
   *  Drives the "Save N Changes" label and visibility of the Save button.
   *  Counts unique (rowId, periodIdx) pairs across both editedMeasures diffs
   *  and clearedCells so a revert that lands on engine==saved still counts. */
  const pendingEditCount = useMemo(() => {
    const counted = new Set<string>();
    for (const [key, measures] of editedMeasures) {
      const row = skuRowById.get(key);
      if (!row) continue;
      const ssp = measures.simulated_shipments_placed_planner_override ?? [];
      const origSsp = row.measures.simulated_shipments_placed_planner_override ?? [];
      for (let i = 0; i < activePeriodColumns.length; i++) {
        if (ssp[i] !== origSsp[i]) counted.add(`${key}|${i}`);
      }
    }
    for (const [key, weekIndices] of clearedCells) {
      for (const ci of weekIndices) counted.add(`${key}|${ci}`);
    }
    return counted.size;
  }, [editedMeasures, clearedCells, skuRowById, activePeriodColumns]);

  /** Post-mutation refresh of ALL active data streams (CHO-678 Phase C +
   *  CHO-693). The full VT is the eventual source while it still loads, but in
   *  collapsed-primary mode the planner is looking at the summary aggregates and
   *  the lazily-fetched detail rows — those must force-recalc too or they show
   *  stale (pre-save) values. Awaited so callers clear the optimistic edit
   *  buffers only once fresh baselines have landed. The detail stream refires
   *  only when SKUs are expanded. */
  const refreshDataStreams = useCallback(async () => {
    const tasks: Promise<void>[] = [
      summaryVt.createViewTransform(summaryVtParams, { forceRecalc: true }),
      skuSummaryVt.createViewTransform(skuSummaryVtParams, { forceRecalc: true }),
    ];
    // The full VT is dormant in lazyDetail mode — don't wake it on save.
    if (!lazyDetail) {
      tasks.push(vt.createViewTransform(vtParams, { forceRecalc: true }));
    }
    if (detailVtParams !== undefined) {
      tasks.push(detailVt.createViewTransform(detailVtParams, { forceRecalc: true }));
    }
    await Promise.all(tasks);
  }, [lazyDetail, vt, vtParams, summaryVt, summaryVtParams, skuSummaryVt, skuSummaryVtParams, detailVt, detailVtParams]);

  /** Persist every pending edit/clear across all retailers in a single
   *  batched mutation, then clear FE buffers and refetch VT. Backend does
   *  one CAS loop on current_edits.json + one status upsert + one recalc
   *  regardless of how many retailers are touched. */
  const persistAllEdits = useCallback(
    async () => {
      if (!runSettingsJson || isSaving) return;

      // For every row the planner touched this session, emit edits per the
      // row's lifecycle:
      //
      //   First save (no prior parquet rows): emit the FULL editable period
      //   vector — real edits (source=user: differs from baseline) and
      //   baseline pass-throughs (source=engine: equals baseline). Engine
      //   pins capture the baseline at save time so intent + lineage survive
      //   future engine re-runs.
      //
      //   Subsequent save (row has prior pins): emit only cells the user
      //   touched this session. Untouched cells stay byte-for-byte in
      //   parquet (same value, same source, same edited_at). Reverts via
      //   the X button emit pushClear; typing the engine value is a no-op.
      //
      //   Restart (row has prior pins, but every cell with a prior user pin
      //   was X-cleared or typed over this session): fall through to
      //   first-save semantics. The user has fully replaced the prior edit
      //   lineage, so re-emit engine pins around the new edit to restore
      //   re-run resilience instead of leaving the row as a delta sliver.
      //
      // In all modes, an all-cleared row (no user intent left post-save)
      // emits clears for every period so parquet drops the row entirely.
      const currentGrainEnum = GRAIN_TO_ENUM[params.skuGrain];
      const edits: RetailerSupplyEditInput[] = [];
      // The sku field on the wire is always a single shipping_sku — the canonical
      // identity used by the resolver for upsert/clear matching. sku_grain is just
      // metadata (which grain the user was editing at). Vividly/stick skus are
      // assumed to be 1:1 with shipping_skus, so contributingShippingSkus[0] is
      // the unique shipping_sku for this row regardless of display grain.
      const shippingSkuField = (row: SkuRow): string =>
        params.skuGrain === "shipping" ? row.sku : row.contributingShippingSkus[0] ?? row.sku;
      const pushClear = (row: SkuRow, periodIdx: number) => {
        edits.push({
          first_receiver: row.firstReceiver,
          // CHO-478 stage 3: planning_level is the wire identity. Buckets 1+3
          // collapse to FR (planning_level == first_receiver); bucket 2 is
          // "<FR> - <DC>". Retailer is no longer sent — gateway doesn't
          // persist it.
          planning_level: row.planningLevel,
          sku: shippingSkuField(row),
          sku_grain: currentGrainEnum,
          period: activePeriodColumns[periodIdx],
          simulated_shipments_placed: null,
        });
      };
      const pushSet = (
        row: SkuRow,
        periodIdx: number,
        value: number,
        source: "user" | "engine",
      ) => {
        edits.push({
          first_receiver: row.firstReceiver,
          planning_level: row.planningLevel,
          sku: shippingSkuField(row),
          sku_grain: currentGrainEnum,
          period: activePeriodColumns[periodIdx],
          simulated_shipments_placed: value,
          source,
        });
      };
      const touchedRowIds = new Set<string>([
        ...editedMeasures.keys(),
        ...clearedCells.keys(),
      ]);

      for (const key of touchedRowIds) {
        const row = skuRowById.get(key);
        if (!row) continue;
        const measures = editedMeasures.get(key) ?? row.measures;
        const editable = measures.simulated_shipments_placed_planner_override ?? [];
        const baseline = row.measures.simulated_shipments_placed_atomic;

        // No engine baseline to compare against → can't derive source. Fall
        // back to delta-only emit (legacy behavior) so we don't accidentally
        // wipe rows that have no atomic row.
        if (!baseline) {
          const origSsp = row.measures.simulated_shipments_placed_planner_override ?? [];
          for (let i = 0; i < activePeriodColumns.length; i++) {
            if (editable[i] === origSsp[i]) continue;
            const isCleared = clearedCells.get(key)?.has(i) ?? false;
            if (isCleared) pushClear(row, i);
            else if (editable[i] !== undefined) pushSet(row, i, editable[i], "user");
          }
          continue;
        }

        // Steady-state invariant: every row in parquet has at least one
        // user pin (engine pins are only created alongside a user edit on
        // first save, and an all-cleared revert wipes the whole row). So a
        // non-null editedAt anywhere in the row signals prior parquet rows.
        const rowEverSaved = row.editedAt.some((t) => t != null);

        if (rowEverSaved) {
          const editedSsp = editedMeasures.get(key)?.simulated_shipments_placed_planner_override;
          const savedSsp = row.measures.simulated_shipments_placed_planner_override ?? [];
          const cleared = clearedCells.get(key);

          // Restart detection: every cell with a prior user pin (editedAt
          // != null) was either X-cleared or typed over this session. The
          // user has fully replaced the prior edit lineage, so fall through
          // to the first-save block below to re-emit engine pins around
          // the new edit. Without this, a "clear everything then re-edit"
          // session would persist as a single delta row with no surrounding
          // engine pins and no protection against future engine re-runs.
          let allSavedCellsTouched = true;
          for (let i = 0; i < activePeriodColumns.length; i++) {
            if (row.editedAt[i] == null) continue;
            if (cleared?.has(i)) continue;
            const sessionTouched =
              editedSsp !== undefined && editedSsp[i] !== savedSsp[i];
            if (!sessionTouched) {
              allSavedCellsTouched = false;
              break;
            }
          }

          if (!allSavedCellsTouched) {
            // Subsequent save: delta-only. Untouched cells stay byte-for-byte
            // in parquet (preserving prior source + value + edited_at).

            // All-cleared check: if no user-source intent remains post-save,
            // wipe the row (matches the first-save allEngine semantics).
            let hasUserPostSave = false;
            for (let i = 0; i < activePeriodColumns.length; i++) {
              if (baseline[i] === undefined) continue;
              if (cleared?.has(i)) continue;
              const sessionTouched =
                editedSsp !== undefined && editedSsp[i] !== savedSsp[i];
              if (sessionTouched) {
                const v = editedSsp![i];
                if (v !== undefined && !sameRounded(v, baseline[i])) {
                  hasUserPostSave = true;
                  break;
                }
                // sessionTouched onto baseline → soft revert; doesn't count.
              } else if (row.editedAt[i] != null) {
                hasUserPostSave = true;
                break;
              }
            }

            if (!hasUserPostSave) {
              for (let i = 0; i < activePeriodColumns.length; i++) {
                if (baseline[i] === undefined) continue;
                pushClear(row, i);
              }
              continue;
            }

            for (let i = 0; i < activePeriodColumns.length; i++) {
              const b = baseline[i];
              if (b === undefined) continue;
              if (cleared?.has(i)) {
                pushClear(row, i);
                continue;
              }
              const sessionTouched =
                editedSsp !== undefined && editedSsp[i] !== savedSsp[i];
              if (!sessionTouched) continue;
              const v = editedSsp![i];
              if (v === undefined) continue;
              if (sameRounded(v, b)) continue; // typed baseline → no-op
              pushSet(row, i, v, "user");
            }
            continue;
          }
          // allSavedCellsTouched: fall through to first-save block.
        }

        // First save (or restart): full-row save with engine pins.
        let allEngine = true;
        for (let i = 0; i < activePeriodColumns.length; i++) {
          if (baseline[i] === undefined) continue;
          if (!sameRounded(editable[i], baseline[i])) { allEngine = false; break; }
        }

        if (allEngine) {
          for (let i = 0; i < activePeriodColumns.length; i++) {
            if (baseline[i] === undefined) continue;
            pushClear(row, i);
          }
          continue;
        }

        for (let i = 0; i < activePeriodColumns.length; i++) {
          const b = baseline[i];
          const e = editable[i];
          if (b === undefined || e === undefined) continue;
          pushSet(row, i, e, sameRounded(e, b) ? "engine" : "user");
        }
      }

      // Nothing to save — no-op. Status transition (→ edited / → ready) is
      // derived from edit state (VT) or persisted via Mark Ready.
      if (edits.length === 0) return;

      // Extract inputId + settingsNameAndVersion from runSettingsJson
      const rs = JSON.parse(runSettingsJson) as {
        input_id: string;
        settings_name_and_version: string;
      };

      setIsSaving(true);
      try {
        await apolloClient.mutate({
          mutation: SaveRetailerSupplyEditsDocument,
          variables: {
            edits,
            inputId: rs.input_id,
            settingsNameAndVersion: rs.settings_name_and_version,
          },
        });

        // Optimistic (CHO-693): keep the local edit buffers in place across the
        // recompute so the grid keeps showing the planner's just-entered values
        // during the ~10s refresh — instead of flashing back to the stale
        // pre-edit baseline. The fresh VT bakes these same edits into its
        // baseline, so the overlaid values match; once it lands we drop the
        // now-redundant buffers (status = edited rehydrates from the VT's status
        // column, derived from has_saved_edits). The edits are already persisted
        // by the mutation above, so clearing is safe even if the refetch errors.
        await refreshDataStreams();
        setEditedMeasures(new Map());
        setClearedCells(new Map());
        setRevertSnapshots(new Map());
      } catch (err) {
        console.error("[RetailerSupply] Failed to save retailer supply edits:", err);
      } finally {
        setIsSaving(false);
      }
    },
    [runSettingsJson, isSaving, editedMeasures, clearedCells, skuRowById, activePeriodColumns, refreshDataStreams, params.skuGrain]
  );

  // ── Bulk CSV upload ───────────────────────────────────────────────────────
  /** Upload a CSV of edits using the presigned-S3-URL pattern:
   *    1. Ask backend for a presigned PUT URL + s3Key (small request).
   *    2. PUT the CSV directly to S3 from the browser (bypasses the GraphQL
   *       request body, no 10MB API Gateway ceiling).
   *    3. Call the mutation with the s3Key — backend reads from S3, parses,
   *       validates, and saves via the same path as manual edits.
   *  CSV is set-only (no clears) and always source="user" — see
   *  docs/plan-retailer-supply-csv-upload.md. */
  const handleCsvUpload = useCallback(
    async (file: File) => {
      if (!runSettingsJson || isSaving || isCsvUploading) return;
      const rs = JSON.parse(runSettingsJson) as {
        input_id: string;
        settings_name_and_version: string;
      };
      // Use a dedicated flag for CSV-upload progress so the button can show
      // its own "Uploading…" state without conflating with a manual Save
      // (which also flips isSaving). Also bump isSaving so anything else
      // gating on "any save in flight" stays consistent.
      setIsCsvUploading(true);
      setIsSaving(true);
      try {
        // 1. Get a presigned PUT URL.
        const urlRes = await apolloClient.query({
          query: GetRetailerSupplyEditsCsvUploadUrlDocument,
          fetchPolicy: "no-cache",
        });
        const slot = urlRes.data?.getRetailerSupplyEditsCsvUploadUrl;
        if (!slot?.uploadUrl || !slot?.s3Key) {
          throw new Error("Failed to obtain a CSV upload URL.");
        }

        // 2. PUT the file straight to S3.
        const putRes = await fetch(slot.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": "text/csv" },
        });
        if (!putRes.ok) {
          throw new Error(`Upload to S3 failed (HTTP ${putRes.status}).`);
        }

        // 3. Tell the backend to ingest from the s3Key. The mutation blocks
        //    until the parquet is written + the BE-side VT cache is
        //    invalidated (triggerSupplyWalkRecalc inside saveRetailerSupplyEdits).
        const result = await apolloClient.mutate({
          mutation: UploadRetailerSupplyEditsCsvDocument,
          variables: {
            s3Key: slot.s3Key,
            inputId: rs.input_id,
            settingsNameAndVersion: rs.settings_name_and_version,
          },
        });
        const payload = result.data?.uploadRetailerSupplyEditsCsv;
        if (!payload?.success) {
          addMessage({
            type: "error",
            title: "CSV upload failed",
            description: payload?.message ?? "Unknown error.",
          });
          return;
        }

        // 4. Force a FE-side VT recalc so the table renders the new edits.
        //    This can take real time on large saves; the success message
        //    fires AFTER so "Saved" matches what the user actually sees.
        await refreshDataStreams();
        addMessage({
          type: "success",
          title: "CSV upload saved",
          description: `Saved edits from ${file.name}.`,
          autoDismiss: true,
          dismissAfterMs: 4000,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        addMessage({
          type: "error",
          title: "CSV upload failed",
          description: msg,
        });
        console.error("[RetailerSupply] CSV upload failed:", err);
      } finally {
        setIsSaving(false);
        setIsCsvUploading(false);
      }
    },
    [runSettingsJson, isSaving, isCsvUploading, addMessage, refreshDataStreams]
  );

  // ── Mark Ready ────────────────────────────────────────────────────────────

  const markSelectedAsReady = useCallback(async () => {
    if (selectedFirstReceivers.size === 0 || !runSettingsJson || markingReady) return;
    const rs = JSON.parse(runSettingsJson) as {
      input_id: string;
      settings_name_and_version: string;
    };
    setMarkingReady(true);
    try {
      await apolloClient.mutate({
        mutation: MarkRetailerSupplyAsReadyDocument,
        variables: {
          firstReceivers: Array.from(selectedFirstReceivers),
          inputId: rs.input_id,
          settingsNameAndVersion: rs.settings_name_and_version,
        },
      });
      setSelectedFirstReceivers(new Set());
      await refreshDataStreams();
    } catch (err) {
      console.error("[RetailerSupply] Failed to mark first receivers as ready:", err);
    } finally {
      setMarkingReady(false);
    }
  }, [selectedFirstReceivers, runSettingsJson, markingReady, refreshDataStreams]);

  const toggleFirstReceiverSelection = useCallback((firstReceiver: string) => {
    setSelectedFirstReceivers((prev) => {
      const next = new Set(prev);
      if (next.has(firstReceiver)) next.delete(firstReceiver);
      else next.add(firstReceiver);
      return next;
    });
  }, []);

  // ── Edit simulated shipments ──────────────────────────────────────────────

  const handleSimEdit = useCallback(
    (row: SkuRow, weekIdx: number, newValue: number, options?: { suppressParityHint?: boolean }) => {
      const currentMeasures = editedMeasures.get(row.id) ?? row.measures;
      const lt = currentMeasures.lead_time_in_weeks?.[0];
      const leadTime = lt != null && lt > 0 ? Math.round(lt) : undefined;
      const updated = recalcMeasures(currentMeasures, weekIdx, newValue, leadTime, row.unitPriceShipping);

      // If the resulting override row matches the SAVED override at every
      // cell, drop the row from `editedMeasures` so the renderer falls back
      // to `row.measures`. We compare to the saved state (not atomic),
      // because clearing a saved override (override→atomic) still needs to
      // shadow row.measures: the parquet-clear hasn't happened yet, so
      // row.measures still has the saved override and would render the
      // saved value as the cell's placeholder ("0" instead of the engine
      // baseline) and leave derived rows on the saved-override walk.
      const savedOverrideRow = row.measures.simulated_shipments_placed_planner_override;
      const overrideRow = updated.simulated_shipments_placed_planner_override;
      const stillEdited = hasAnyNetOverride(savedOverrideRow, overrideRow);

      setEditedMeasures((prev) => {
        const next = new Map(prev);
        if (stillEdited) next.set(row.id, updated);
        else next.delete(row.id);
        return next;
      });

      if (!stillEdited) {
        // Going net-zero also drops the clearedCells entry for this row —
        // handleClearEdit re-adds it after this call when the cleared cell
        // had a saved override (parquet-clear semantics).
        setClearedCells((prev) => {
          if (!prev.has(row.id)) return prev;
          const next = new Map(prev);
          next.delete(row.id);
          return next;
        });
        // Invalidate cached top-panel summaries / status / diff-periods for
        // this row's first-receiver. The caches gate on dirtyFirstReceivers,
        // which on the next render won't include this FR (we just removed
        // the only edited row under it) — so the cache hit would return the
        // dirty summary we computed during the edit. Direct delete forces
        // the next render to recompute from row.measures.
        const fr = row.firstReceiver;
        summariesCacheRef.current?.map.delete(fr);
        statusesCacheRef.current?.map.delete(fr);
        diffPeriodsCacheRef.current?.map.delete(fr);
        // Same hazard for the bottom-panel SKU summary cache (gated on
        // dirtySkus). Without this delete, typing a cell back to its saved
        // value leaves the SKU header showing the prior dirty aggregate.
        skuSummariesCacheRef.current?.map.delete(row.sku);
      } else {
        // If the typed value isn't the engine baseline, drop any queued clear
        // for this cell — the user is overriding the X-revert with a real edit,
        // and the save mutation honors clearedCells, so a stale entry would
        // silently turn the typed value into a parquet drop.
        const atomic = row.measures.simulated_shipments_placed_atomic?.[weekIdx];
        if (atomic !== undefined && !sameRounded(newValue, atomic)) {
          setClearedCells((prev) => {
            const set = prev.get(row.id);
            if (!set?.has(weekIdx)) return prev;
            const next = new Map(prev);
            const nextSet = new Set(set);
            nextSet.delete(weekIdx);
            if (nextSet.size === 0) next.delete(row.id);
            else next.set(row.id, nextSet);
            return next;
          });
        }
      }

      // Re-evaluate whether this edit produces a surplus and, if so, anchor
      // the suggestion to this cell. The chip's cells are derived on render
      // (see `parityHint` useMemo) — we only need to know whether to pin
      // the chip here. Same row + no surplus clears the existing chip; a
      // chip on a different row is left alone (planner may be alternating).
      // `suppressParityHint` short-circuits this for apply-step edits, so
      // chained writes inside handleApplyParityHint don't fight the anchor.
      if (!options?.suppressParityHint) {
        const baseline = row.measures.simulated_shipments_placed_atomic;
        if (baseline) {
          const savedOverride = row.measures.simulated_shipments_placed_planner_override ?? [];
          // Treat X-cleared cells as if they're at baseline so a parity walk
          // after a row revert still finds eligible reducible cells.
          const effectiveSaved = effectiveSavedForParity(
            savedOverride,
            baseline,
            clearedCells.get(row.id),
          );
          const cells = suggestParityRun(
            baseline,
            updated.simulated_shipments_placed_planner_override ?? [],
            effectiveSaved,
            weekIdx,
          );
          if (cells.length > 0) {
            setParityHintAnchor({ rowId: row.id, editedWeekIdx: weekIdx });
          } else {
            setParityHintAnchor((prev) => (prev?.rowId === row.id ? null : prev));
          }
        }
      }
    },
    [editedMeasures, clearedCells]
  );

  const handleClearEdit = useCallback(
    (row: SkuRow, weekIdx: number) => {
      const engineVal = row.measures.simulated_shipments_placed_atomic?.[weekIdx]
        ?? originalMeasuresMap.get(row.id)?.simulated_shipments_placed_planner_override?.[weekIdx]
        ?? 0;
      // Revert display to engine value and recalc downstream — suppress the
      // parity hint since clearing isn't a "raise above baseline" event.
      handleSimEdit(row, weekIdx, engineVal, { suppressParityHint: true });
      setParityHintAnchor((prev) =>
        prev?.rowId === row.id && prev.editedWeekIdx === weekIdx ? null : prev
      );
      // Only queue a parquet-clear if there's actually a SAVED override at
      // this cell. Clearing a purely local (unsaved) edit is a no-op on disk,
      // so it shouldn't contribute to the "Save N Changes" counter.
      const savedOverride = row.measures.simulated_shipments_placed_planner_override?.[weekIdx];
      const atomic = row.measures.simulated_shipments_placed_atomic?.[weekIdx];
      const hasSavedOverride = savedOverride !== undefined && savedOverride !== atomic;
      if (hasSavedOverride) {
        setClearedCells((prev) => {
          const next = new Map(prev);
          const set = new Set(next.get(row.id) ?? []);
          set.add(weekIdx);
          next.set(row.id, set);
          return next;
        });
      }
    },
    [handleSimEdit, originalMeasuresMap]
  );

  /** Queue revert for every overridden cell (saved or local) across the given
   *  rows in a single batched state update. "Overridden" = atomic baseline
   *  differs from the effective planner override, which covers saved edits
   *  (with or without `edited_at`) and unsaved local edits. The cell's
   *  effective value is recalc'd back to the engine baseline and its index
   *  added to `clearedCells` so the save mutation emits a clear record. A
   *  single reducer call per state bucket keeps this safe across many rows
   *  without stale-closure problems. */
  const handleRevertRows = useCallback(
    (rows: SkuRow[]) => {
      if (rows.length === 0) return;
      if (!params.applyEdits) return;

      // Snapshot pre-revert state so SkuDetailRow can offer "Restore" until
      // the next save. Only snapshot rows that actually have something to
      // revert — matches the skip logic in the setState updaters below.
      setRevertSnapshots((prevSnap) => {
        const nextSnap = new Map(prevSnap);
        for (const row of rows) {
          const current = editedMeasuresRef.current.get(row.id) ?? row.measures;
          const engineSsp = row.measures.simulated_shipments_placed_atomic ?? [];
          const curSsp = current.simulated_shipments_placed_planner_override ?? [];
          let hasAnyDiff = false;
          const nLen = Math.max(engineSsp.length, curSsp.length);
          for (let i = 0; i < nLen; i++) {
            if (engineSsp[i] === undefined) continue;
            if (engineSsp[i] !== curSsp[i]) {
              hasAnyDiff = true;
              break;
            }
          }
          if (!hasAnyDiff) continue;
          const clearsSnap = clearedCellsRef.current.get(row.id);
          // Defensive copy of the measures record (and each inner array) so a
          // future in-place mutator on editedMeasures values can't silently
          // corrupt the snapshot. Mirrors the `new Set(clearsSnap)` copy below.
          const liveMeasures = editedMeasuresRef.current.get(row.id);
          const measuresSnap = liveMeasures
            ? Object.fromEntries(
                Object.entries(liveMeasures).map(([k, v]) => [k, [...v]]),
              )
            : undefined;
          nextSnap.set(row.id, {
            measures: measuresSnap,
            clears: clearsSnap ? new Set(clearsSnap) : undefined,
          });
        }
        return nextSnap;
      });

      setEditedMeasures((prevEdited) => {
        const nextEdited = new Map(prevEdited);
        for (const row of rows) {
          const current = nextEdited.get(row.id) ?? row.measures;
          const engineSsp = row.measures.simulated_shipments_placed_atomic ?? [];
          const origSsp = row.measures.simulated_shipments_placed_planner_override ?? [];
          const curSsp = current.simulated_shipments_placed_planner_override ?? [];

          const idxs = new Set<number>();
          const nLen = Math.max(engineSsp.length, curSsp.length);
          for (let i = 0; i < nLen; i++) {
            if (engineSsp[i] === undefined) continue;
            if (engineSsp[i] !== curSsp[i]) idxs.add(i);
          }
          if (idxs.size === 0) continue;

          const lt = current.lead_time_in_weeks?.[0];
          const leadTime = lt != null && lt > 0 ? Math.round(lt) : undefined;
          let next = current;
          for (const idx of idxs) {
            const eng = engineSsp[idx] ?? origSsp[idx] ?? 0;
            next = recalcMeasures(next, idx, eng, leadTime, row.unitPriceShipping);
          }
          // After reverting every diverging cell, the row matches the engine
          // atomic baseline. Drop the editedMeasures entry only if the saved
          // override also matches atomic at every cell — otherwise the entry
          // is still needed to shadow row.measures (the parquet-clear is
          // queued in clearedCells but hasn't been persisted yet). Parity
          // with single-cell handleSimEdit revert.
          const savedSspForCheck = row.measures.simulated_shipments_placed_planner_override;
          if (hasAnyNetOverride(savedSspForCheck, next.simulated_shipments_placed_planner_override)) {
            nextEdited.set(row.id, next);
          } else {
            nextEdited.delete(row.id);
            // Invalidate top-panel cache entries for this row's FR — same
            // reason as in handleSimEdit: dirtyFirstReceivers no longer
            // flags this FR but the cache still holds the dirty summary.
            const fr = row.firstReceiver;
            summariesCacheRef.current?.map.delete(fr);
            statusesCacheRef.current?.map.delete(fr);
            diffPeriodsCacheRef.current?.map.delete(fr);
            skuSummariesCacheRef.current?.map.delete(row.sku);
          }
        }
        return nextEdited;
      });

      setClearedCells((prev) => {
        const nextMap = new Map(prev);
        for (const row of rows) {
          const set = new Set(nextMap.get(row.id) ?? []);
          const engineSsp = row.measures.simulated_shipments_placed_atomic ?? [];
          const origSsp = row.measures.simulated_shipments_placed_planner_override ?? [];
          for (let i = 0; i < engineSsp.length; i++) {
            if (engineSsp[i] === undefined) continue;
            if (engineSsp[i] !== origSsp[i]) set.add(i);
          }
          for (let i = 0; i < row.editedAt.length; i++) {
            if (row.editedAt[i] != null) set.add(i);
          }
          if (set.size > 0) nextMap.set(row.id, set);
        }
        return nextMap;
      });
    },
    [params.applyEdits]
  );

  const handleRevertRow = useCallback((row: SkuRow) => handleRevertRows([row]), [handleRevertRows]);

  /** Re-apply the snapshot captured by the most recent handleRevertRow for
   *  this row, then drop the snapshot so the Restore button disappears. The
   *  cache invalidations skipped here on purpose: handleRevertRows deletes
   *  FR cache entries when it nulls out an editedMeasures entry, so those
   *  caches are already empty; the next read after restore recomputes
   *  against the restored override. */
  const handleRestoreRow = useCallback((row: SkuRow) => {
    if (!applyEditsRef.current) return;
    const snap = revertSnapshotsRef.current.get(row.id);
    if (!snap) return;
    setEditedMeasures((prev) => {
      const next = new Map(prev);
      if (snap.measures) next.set(row.id, snap.measures);
      else next.delete(row.id);
      return next;
    });
    setClearedCells((prev) => {
      const next = new Map(prev);
      if (snap.clears && snap.clears.size > 0) next.set(row.id, snap.clears);
      else next.delete(row.id);
      return next;
    });
    setRevertSnapshots((prev) => {
      const next = new Map(prev);
      next.delete(row.id);
      return next;
    });
  }, []);

  /** Apply the parity-run suggestion: chain all suggested cell edits in a
   *  single setEditedMeasures transition so each step sees the prior step's
   *  result. A naive per-cell loop calling handleSimEdit hits a stale-closure
   *  bug — every call reads the same captured editedMeasures, so only the
   *  last iteration's edit survives. The functional updater here always sees
   *  the freshest state.
   *
   *  Cells are recomputed from baseline + current override at click time
   *  rather than read from snapshotted state, so what we apply matches what
   *  the chip is currently showing the user — even if a VT refetch shifted
   *  the baseline since the chip first appeared.
   *
   *  Also drops any queued clears on the just-edited cells (stale entries
   *  would otherwise turn a typed override into a parquet drop on save). */
  const handleApplyParityHint = useCallback(
    (row: SkuRow) => {
      if (!parityHintAnchor || parityHintAnchor.rowId !== row.id) return;
      const baseline = row.measures.simulated_shipments_placed_atomic;
      if (!baseline) return;
      const savedOverride = row.measures.simulated_shipments_placed_planner_override ?? [];
      const override =
        editedMeasures.get(row.id)?.simulated_shipments_placed_planner_override
        ?? savedOverride;
      const effectiveSaved = effectiveSavedForParity(
        savedOverride,
        baseline,
        clearedCells.get(row.id),
      );
      const cells = suggestParityRun(baseline, override, effectiveSaved, parityHintAnchor.editedWeekIdx);
      if (cells.length === 0) {
        setParityHintAnchor(null);
        return;
      }
      setEditedMeasures((prev) => {
        const cur = prev.get(row.id) ?? row.measures;
        const lt = cur.lead_time_in_weeks?.[0];
        const leadTime = lt != null && lt > 0 ? Math.round(lt) : undefined;
        let next = cur;
        for (const c of cells) {
          next = recalcMeasures(next, c.weekIdx, c.value, leadTime, row.unitPriceShipping);
        }
        return new Map(prev).set(row.id, next);
      });
      setClearedCells((prev) => {
        const set = prev.get(row.id);
        if (!set) return prev;
        let mutated = false;
        const nextSet = new Set(set);
        for (const c of cells) {
          if (nextSet.delete(c.weekIdx)) mutated = true;
        }
        if (!mutated) return prev;
        const next = new Map(prev);
        if (nextSet.size === 0) next.delete(row.id);
        else next.set(row.id, nextSet);
        return next;
      });
      setParityHintAnchor(null);
    },
    [parityHintAnchor, editedMeasures, clearedCells]
  );

  // Ref-shimmed wrappers: handleSimEdit / handleClearEdit recreate on every
  // editedMeasures change. Passing them directly to memo'd row components
  // would defeat the memo. The shims hold stable identity but always invoke
  // the latest handler via the ref, so closures see fresh state.
  // The applyEdits ref lets the shims short-circuit edit-producing actions
  // when the user is in engine-only view (CHO-454) without losing identity.
  const applyEditsRef = useRef(params.applyEdits);
  applyEditsRef.current = params.applyEdits;
  const handleSimEditRef = useRef(handleSimEdit);
  handleSimEditRef.current = handleSimEdit;
  const stableHandleSimEdit = useCallback(
    (row: SkuRow, weekIdx: number, newValue: number) => {
      if (!applyEditsRef.current) return;
      handleSimEditRef.current(row, weekIdx, newValue);
    },
    [],
  );

  const handleClearEditRef = useRef(handleClearEdit);
  handleClearEditRef.current = handleClearEdit;
  const stableHandleClearEdit = useCallback(
    (row: SkuRow, weekIdx: number) => {
      if (!applyEditsRef.current) return;
      handleClearEditRef.current(row, weekIdx);
    },
    [],
  );

  const handleApplyParityHintRef = useRef(handleApplyParityHint);
  handleApplyParityHintRef.current = handleApplyParityHint;
  const stableHandleApplyParityHint = useCallback(
    (row: SkuRow) => {
      if (!applyEditsRef.current) return;
      handleApplyParityHintRef.current(row);
    },
    [],
  );
  const stableDismissParityHint = useCallback(() => setParityHintAnchor(null), []);

  // ── Retailer panel columns ────────────────────────────────────────────────

  const visibleFirstReceiverKeys = useMemo(
    () => allFirstReceiverSummaries.map((r) => r.firstReceiver),
    [allFirstReceiverSummaries]
  );
  const allVisibleSelected =
    visibleFirstReceiverKeys.length > 0 &&
    visibleFirstReceiverKeys.every((k) => selectedFirstReceivers.has(k));
  const someVisibleSelected =
    !allVisibleSelected &&
    visibleFirstReceiverKeys.some((k) => selectedFirstReceivers.has(k));

  const toggleAllVisible = useCallback(() => {
    setSelectedFirstReceivers((prev) => {
      if (visibleFirstReceiverKeys.every((k) => prev.has(k))) {
        const next = new Set(prev);
        for (const k of visibleFirstReceiverKeys) next.delete(k);
        return next;
      }
      const next = new Set(prev);
      for (const k of visibleFirstReceiverKeys) next.add(k);
      return next;
    });
  }, [visibleFirstReceiverKeys]);

  const retailerColumns = useMemo<ColumnDef[]>(() => {
    const checkboxCol: ColumnDef = {
      key: "_checkbox",
      header: (
        <input
          type="checkbox"
          css={s.checkboxInput}
          checked={allVisibleSelected}
          ref={(el) => {
            if (el) el.indeterminate = someVisibleSelected;
          }}
          onChange={toggleAllVisible}
          aria-label="Select all first receivers"
        />
      ),
      css: s.checkboxHeaderCell,
    };
    const markedReadyCol: ColumnDef = {
      key: "status_updated_at",
      header: "Marked Ready",
      sortKey: "status_updated_at",
      css: s.markedReadyHeaderCell,
    };
    const statusCol: ColumnDef = {
      key: "_status",
      header: "",
      css: css`
        position: sticky;
        left: ${CHECKBOX_COL_W}px;
        z-index: 3;
        min-width: ${STATUS_COL_W}px;
        max-width: ${STATUS_COL_W}px;
        background: ${theme.colors.muted};
        text-align: center;
      `,
    };
    const labelCol: ColumnDef = {
      key: "first_receiver",
      header: "First Receiver",
      sortKey: "first_receiver",
      css: css`
        position: sticky;
        left: ${CHECKBOX_COL_W + STATUS_COL_W}px;
        z-index: 3;
        min-width: var(--sku-col-w, ${SKU_COL_W}px);
        max-width: var(--sku-col-w, ${SKU_COL_W}px);
        background: ${theme.colors.muted};
      `,
    };
    return buildTimeSeriesColumns([checkboxCol, statusCol, labelCol, markedReadyCol], activeVisiblePeriodColumns, { pastPeriodSet: activePastPeriodSet });
  }, [activeVisiblePeriodColumns, activePastPeriodSet, actualsExpanded, hasActuals, allVisibleSelected, someVisibleSelected, toggleAllVisible]);

  // ── Summary-mode top panel (CHO-678 OQ3 Variant 2) ────────────────────────
  // A SELF-CONTAINED, read-only rendering of the top panel driven by the lean
  // summary VT, shown only while !fullReady. No checkbox / Mark-Ready / status
  // column (those need the full per-SKU VT) and no drilldown (the SKU detail it
  // would open is the full VT) — rows are non-interactive until the full VT
  // backfills and the existing path takes over.
  const summaryHeaderGroups = useMemo<HeaderGroup[]>(
    () =>
      buildHeaderGroups(1, {
        periods: summaryPeriodColumns,
        past: summaryPastPeriodSet,
        visible: summaryVisiblePeriodColumns,
      }),
    [buildHeaderGroups, summaryPeriodColumns, summaryPastPeriodSet, summaryVisiblePeriodColumns],
  );
  const summaryColumns = useMemo<ColumnDef[]>(() => {
    const labelCol: ColumnDef = {
      key: "first_receiver",
      header: "First Receiver",
      css: css`
        position: sticky;
        left: 0;
        z-index: 3;
        min-width: var(--sku-col-w, ${SKU_COL_W}px);
        max-width: var(--sku-col-w, ${SKU_COL_W}px);
        background: ${theme.colors.muted};
      `,
    };
    return buildTimeSeriesColumns([labelCol], summaryVisiblePeriodColumns, {
      pastPeriodSet: summaryPastPeriodSet,
    });
  }, [summaryVisiblePeriodColumns, summaryPastPeriodSet]);

  // Read-only summary cell renderer. Reuses the same DOS/grain formatting AND
  // WOS heatmap as the full path (server now emits the per-FR target WOS), but
  // without the status dot, checkbox, gap badge, or edit-diff shading — those
  // all need the full VT. The heatmap matches the full path's threshold so DOS
  // cell colors don't flip when the full VT backfills.
  const renderSummaryFirstReceiverCells = useCallback(
    (row: FirstReceiverSummary) => {
      const hasAgg = row.aggregateValues.some((v) => v != null);
      const total = row.aggregateValues.reduce((a, v) => a + (v ?? 0), 0);
      const isDOS = params.topAggregateBy === "ending_days_of_supply";
      const isDelta = params.topAggregateBy === MEASURE.FORECAST_SHIPMENTS_DELTA;
      return (
        <>
          <TableCell css={s.topFirstReceiverCell}>
            <span css={css`overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}>
              {row.firstReceiver}
            </span>
          </TableCell>
          {row.aggregateValues.map((v, ci) => {
            const period = summaryPeriodColumns[ci];
            if (!summaryVisiblePeriodColumns.includes(period)) return null;
            const isPast = summaryPastPeriodSet.has(period);
            // WOS heatmap on DOS cells, falling back to the past/forecast bg —
            // same precedence as the full renderer (RetailerSupplyView.tsx:2382).
            const bg = isDOS && v != null
              ? (wosCellBg(v, row.targetWOS) || (isPast ? theme.colors.pastPeriodBg : "transparent"))
              : (isPast ? theme.colors.pastPeriodBg : "transparent");
            const cellColor = isDelta && v != null
              ? v > 0 ? theme.colors.status.success : v < 0 ? theme.colors.status.error : theme.colors.foreground
              : theme.colors.foreground;
            return (
              <TableCell
                key={ci}
                css={css`
                  width: 5rem;
                  min-width: 5rem;
                  max-width: 5rem;
                  padding: 0.5rem 0.75rem;
                  text-align: right;
                  font-variant-numeric: tabular-nums;
                  color: ${cellColor};
                  font-weight: 400;
                  background-color: ${bg};
                `}
              >
                {isDOS ? formatWOS(v) : formatGrainValue(v)}
              </TableCell>
            );
          })}
          <TableCell
            css={
              isDelta
                ? css`
                    ${s.retailerAvgCell};
                    color: ${total > 0 ? theme.colors.status.success : total < 0 ? theme.colors.status.error : theme.colors.foreground};
                  `
                : s.retailerAvgCell
            }
          >
            {!hasAgg
              ? "-"
              : isDOS
                ? formatWOS(total / Math.max(1, row.aggregateValues.length))
                : formatGrainValue(total)}
          </TableCell>
        </>
      );
    },
    [params.topAggregateBy, summaryPeriodColumns, summaryVisiblePeriodColumns, summaryPastPeriodSet],
  );

  // ── Retailer panel cell rendering ─────────────────────────────────────────

  const renderFirstReceiverCells = useCallback(
    (row: FirstReceiverSummary) => {
      // All-null aggregate (suppressed measure on a fallback FR) → show "—" in
      // the avg/total column too, not a misleading 0.
      const hasAgg = row.aggregateValues.some((v) => v != null);
      const total = row.aggregateValues.reduce((a, v) => a + (v ?? 0), 0);
      const toggle = () =>
        setSelectedFirstReceiver((prev) => (prev === row.firstReceiver ? null : row.firstReceiver));
      const groupStatus = firstReceiverStatuses.get(row.firstReceiver) ?? row.status;
      const statusCfg = STATUS_CONFIG[groupStatus];
      const isDOS = params.topAggregateBy === "ending_days_of_supply";
      const isChecked = selectedFirstReceivers.has(row.firstReceiver);

      const markedReadyDate = groupStatus === "ready" && row.statusUpdatedAt
        ? formatDisplayDate(row.statusUpdatedAt, { year: "2-digit", month: "2-digit", day: "2-digit" })
        : "";

      return (
        <>
          <TableCell css={s.checkboxCell} onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              css={s.checkboxInput}
              checked={isChecked}
              onChange={() => toggleFirstReceiverSelection(row.firstReceiver)}
              aria-label={`Select ${row.firstReceiver}`}
            />
          </TableCell>

          <TableCell css={s.topStatusCell} onClick={toggle}>
            <PortalTooltip text={statusCfg.label}>
              <span css={s.statusDotSmall(statusCfg.color)} />
            </PortalTooltip>
          </TableCell>

          <TableCell onClick={toggle} css={s.topFirstReceiverCell}>
            <span css={css`display: inline-flex; align-items: center; gap: 0.375rem; min-width: 0;`}>
              <span css={css`overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}>
                {row.firstReceiver}
              </span>
              {(() => {
                const gap = firstReceiverGaps.get(row.firstReceiver);
                if (!gap) return null;
                return (
                  <span onClick={(e) => e.stopPropagation()} css={css`flex-shrink: 0;`}>
                    {/* Badge shows the number of gap *types* (small); the raw
                        incomplete-row counts live in the tooltip messages.
                        Tooltip is just the gap lines (no header) to keep the
                        popup compact; clicking still routes to the Mappings tab. */}
                    <WarningIcon
                      count={gap.messages.length}
                      messages={gap.messages}
                      onClick={() => navigate("/data-hub")}
                    />
                  </span>
                );
              })()}
            </span>
          </TableCell>

          <TableCell css={s.markedReadyCell} onClick={toggle}>
            {markedReadyDate}
          </TableCell>

          {row.aggregateValues.map((v, ci) => {
            if (!activeVisiblePeriodSet.has(activePeriodColumns[ci])) return null;
            const isPast = activePastPeriodSet.has(activePeriodColumns[ci]);
            // When the top aggregate is either the baseline or editable
            // shipment row, paint cells with an underlying editable/baseline
            // diff the same darker grey used in the expanded detail — a
            // collapsed row still signals where edits are, whether you're
            // viewing the engine or the override aggregate.
            const isShipmentAgg =
              params.topAggregateBy === "simulated_shipments_placed_atomic" ||
              params.topAggregateBy === "simulated_shipments_placed_planner_override";
            const isShipmentDiff = isShipmentAgg &&
              (firstReceiverDiffPeriods.get(row.firstReceiver)?.has(ci) ?? false);
            const bg = isShipmentDiff
              ? theme.colors.gray200
              : isDOS && v != null
                ? (wosCellBg(v, row.targetWOS) || (isPast ? theme.colors.pastPeriodBg : "transparent"))
                : (isPast ? theme.colors.pastPeriodBg : "transparent");
            const isDelta = params.topAggregateBy === MEASURE.FORECAST_SHIPMENTS_DELTA;
            const cellColor = isDelta
              ? v > 0 ? theme.colors.status.success : v < 0 ? theme.colors.status.error : theme.colors.foreground
              : theme.colors.foreground;
            return (
              <TableCell
                key={ci}
                onClick={toggle}
                css={css`
                  width: 5rem;
                  min-width: 5rem;
                  max-width: 5rem;
                  padding: 0.5rem 0.75rem;
                  text-align: right;
                  font-variant-numeric: tabular-nums;
                  color: ${cellColor};
                  font-weight: 400;
                  cursor: pointer;
                  background-color: ${bg};
                `}
              >
                {isDOS ? formatWOS(v) : formatGrainValue(v)}
              </TableCell>
            );
          })}

          <TableCell
            onClick={toggle}
            css={
              params.topAggregateBy === MEASURE.FORECAST_SHIPMENTS_DELTA
                ? css`
                    ${s.retailerAvgCell};
                    color: ${total > 0 ? theme.colors.status.success : total < 0 ? theme.colors.status.error : theme.colors.foreground};
                  `
                : s.retailerAvgCell
            }
          >
            {!hasAgg
              ? "-"
              : isDOS
                ? formatWOS(total / Math.max(1, row.aggregateValues.length))
                : formatGrainValue(total)}
          </TableCell>
        </>
      );
    },
    [activePastPeriodSet, activePeriodColumns, activeVisiblePeriodSet, actualsExpanded, hasActuals, params.topAggregateBy, selectedFirstReceivers, toggleFirstReceiverSelection, firstReceiverStatuses, firstReceiverDiffPeriods, firstReceiverGaps, navigate]
  );

  // ── SKU panel columns (uses buildTimeSeriesColumns like Supply Walk) ──────

  const skuColumns = useMemo<ColumnDef[]>(() => {
    // Bottom table is [_expand, _status, SKU, Planning Level, periods…].
    // SKU now sits in col 3 (the outer nesting level — header rows always
    // visible, PL rows below when expanded). Planning Level moved to col 4
    // and is the last sticky → carries the right-edge shadow. Widths come
    // from --bottom-sku-col-w / --bottom-pl-col-w (computed independently
    // of the top table's sticky widths).
    const labelCols: ColumnDef[] = [
      {
        key: "_expand",
        header: "",
        css: css`
          position: sticky;
          left: 0;
          z-index: 3;
          min-width: ${CHECKBOX_COL_W}px;
          max-width: ${CHECKBOX_COL_W}px;
          background: ${theme.colors.muted};
        `,
      },
      {
        key: "_status",
        header: "",
        css: css`
          position: sticky;
          left: ${CHECKBOX_COL_W}px;
          z-index: 3;
          min-width: ${STATUS_COL_W}px;
          max-width: ${STATUS_COL_W}px;
          background: ${theme.colors.muted};
          text-align: center;
        `,
      },
      {
        key: "sku",
        header: "SKU",
        sortKey: "sku",
        css: css`
          position: sticky;
          left: ${CHECKBOX_COL_W + STATUS_COL_W}px;
          z-index: 3;
          min-width: var(--bottom-sku-col-w, ${MARKED_READY_COL_W}px);
          max-width: var(--bottom-sku-col-w, ${MARKED_READY_COL_W}px);
          background: ${theme.colors.muted};
        `,
      },
      {
        key: "planning_level",
        header: "Planning Level",
        sortKey: "planning_level",
        css: css`
          position: sticky;
          left: calc(${CHECKBOX_COL_W + STATUS_COL_W}px + var(--bottom-sku-col-w, ${MARKED_READY_COL_W}px));
          z-index: 3;
          min-width: var(--bottom-pl-col-w, ${SKU_COL_W}px);
          max-width: var(--bottom-pl-col-w, ${SKU_COL_W}px);
          background: ${theme.colors.muted};
          box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.08);
        `,
      },
    ];
    return buildTimeSeriesColumns(labelCols, activeVisiblePeriodColumns, { pastPeriodSet: activePastPeriodSet });
  }, [activeVisiblePeriodColumns, activePastPeriodSet]);

  // ── SKU panel cell rendering (matches Supply Walk renderCells) ────────────

  const getSkuRowId = useCallback((item: BottomItem) => item.id, []);

  const renderSkuCells = useCallback(
    (item: BottomItem, _isExpanded: boolean, toggle: () => void) => {
      if (item.kind === "header") {
        // SKU aggregate row — always visible for multi-PL SKUs (single-PL
        // SKUs render as a standalone PL row, see bottomItems). Click
        // anywhere toggles expansion (revealing/hiding the SKU's PL rows).
        // We ignore DataTable's `toggle` because it would flip DataTable's
        // internal expandedRows for this id; this row never has detail-keys,
        // so reading our own state for the chevron icon is the single source
        // of truth.
        // Summary-mode headers toggle the lazy-detail expansion set; full-path
        // headers toggle the client-side PL nesting.
        const onClick = () => (item.isSummary ? toggleSummarySku(item.sku) : toggleSku(item.sku));
        const statusCfg = STATUS_CONFIG[item.status];
        const isDOS = params.bottomAggregateBy === "ending_days_of_supply";
        const hasAgg = item.summary.aggregateValues.some((v) => v != null);
        const total = item.summary.aggregateValues.reduce((a, v) => a + (v ?? 0), 0);
        // Mirror SkuRow's currency/WOS dispatch — the header must format
        // values the same way the PL rows below do, or selecting Gross Sales
        // (etc.) would show raw integers up top and $-formatted values below.
        const formatAgg = isDOS
          ? formatWOS
          : MEASURE_FORMAT[params.bottomAggregateBy] === "currency"
            ? (v: number) => formatCurrencyOrNull(v)
            : formatGrainValue;
        // When expanded, override the sticky cells' default white bg so the
        // blue50 row tint reads continuously across the left sticky region
        // instead of cutting off at the period columns.
        const openBg = item.isOpen
          ? css`background: ${theme.colors.blue50};`
          : undefined;
        return (
          <>
            <TableCell css={[s.skuExpandCell, openBg]} onClick={onClick}>
              {item.isOpen ? <ChevronDown css={s.skuChevron} /> : <ChevronRight css={s.skuChevron} />}
            </TableCell>
            <TableCell css={[s.statusCell, openBg]} onClick={onClick}>
              <PortalTooltip text={statusCfg.label}>
                <span css={s.statusDotSmall(statusCfg.color)} />
              </PortalTooltip>
            </TableCell>
            <TableCell css={[s.skuNameCell(false), openBg]} onClick={onClick}>
              <span css={s.skuHeaderName}>{item.sku}</span>
            </TableCell>
            <TableCell css={[s.skuPlanningLevelCell(false), openBg]} onClick={onClick}>
              {item.plCount > 1 && (
                <span css={s.skuHeaderPlCountBadge}>{item.plCount}</span>
              )}
            </TableCell>
            {item.summary.aggregateValues.map((v, ci) => {
              if (!activeVisiblePeriodSet.has(activePeriodColumns[ci])) return null;
              const isPast = activePastPeriodSet.has(activePeriodColumns[ci]);
              let bg = isPast ? theme.colors.pastPeriodBg : "";
              if (isDOS && v != null) bg = wosCellBg(v, item.summary.targetWOS) || bg;
              return (
                <TableCell
                  key={ci}
                  onClick={onClick}
                  css={css`
                    ${s.skuValueCell};
                    background-color: ${bg || "transparent"};
                  `}
                >
                  {v == null ? "-" : formatAgg(v)}
                </TableCell>
              );
            })}
            <TableCell css={s.skuTotalCell} onClick={onClick}>
              {!hasAgg
                ? "-"
                : isDOS
                  ? formatWOS(total / Math.max(1, item.summary.aggregateValues.length))
                  : formatAgg(total)}
            </TableCell>
          </>
        );
      }
      // PL row — `standalone` is true when this is the only PL for its SKU
      // (no header rendered above), so SkuRow restores the SKU as the row's
      // primary label and drops the nesting accent.
      return (
        <SkuRowComponent
          row={item.row}
          isExpanded={_isExpanded}
          toggle={toggle}
          aggKey={params.bottomAggregateBy}
          editsForRow={editedMeasures.get(item.row.id)}
          clearsForRow={clearedCells.get(item.row.id)}
          groupStatus={firstReceiverStatuses.get(item.row.firstReceiver) ?? item.row.status}
          visiblePeriodSet={activeVisiblePeriodSet}
          periodColumns={activePeriodColumns}
          pastPeriodSet={activePastPeriodSet}
          standalone={item.standalone}
        />
      );
    },
    [params.bottomAggregateBy, editedMeasures, clearedCells, firstReceiverStatuses, activeVisiblePeriodSet, activePeriodColumns, activePastPeriodSet, toggleSku, toggleSummarySku],
  );

  // ── SKU detail rows (uses getDetailKeys/renderDetailRow like Supply Walk) ─

  const getSkuDetailKeys = useCallback(
    (item: BottomItem) => {
      // Header rows never own a measure walk — return [] so DataTable can't
      // expand them. Toggling a header row is handled by our own onClick.
      if (item.kind === "header") return [];
      const row = item.row;
      const isFallback = row.iohSource === "netsuite_fallback";
      const keys = MEASURE_KEYS.filter((mk) => {
        if (!row.measures[mk] || row.measures[mk].length === 0) return false;
        if (!actualsExpanded && ACTUALS_MEASURE_KEYS.has(mk)) return false;
        if (mk === "unfulfilled_demand") return false;
        // Actual Shipments are folded into past-period cells of the Forecast
        // Shipments rows (see SkuDetailRow), so the standalone row is hidden.
        if (mk === "open_shipments_placed") return false;
        // CHO-610 follow-up: NetSuite-fallback rows have null
        // starting_inventory / actual_sales / ending_inventory /
        // ending_days_of_supply (the engine deliberately suppresses them —
        // no real IOH anchor). Skip those measure rows entirely for fallback
        // rows so the expanded walk doesn't render four "—" rows of noise.
        if (isFallback && FALLBACK_SUPPRESSED_KEYS.has(mk)) return false;
        return true;
      }) as string[];
      if (row.promotions.some((p) => p != null)) keys.push("promotions");
      return keys;
    },
    [actualsExpanded]
  );

  const renderSkuDetailRow = useCallback(
    (item: BottomItem, mk: string) => {
      // Defensive: getSkuDetailKeys returns [] for headers, so this branch is
      // unreachable in normal flow. The narrow keeps TypeScript happy.
      if (item.kind === "header") return null;
      const row = item.row;
      return (
        <SkuDetailRowComponent
          row={row}
          mk={mk}
          editsForRow={editedMeasures.get(row.id)}
          clearsForRow={clearedCells.get(row.id)}
          originalMeasuresForRow={originalMeasuresMap.get(row.id)}
          canRestore={revertSnapshots.has(row.id)}
          visiblePeriodSet={activeVisiblePeriodSet}
          periodColumns={activePeriodColumns}
          pastPeriodSet={activePastPeriodSet}
          activePromoPeriodIdx={activePromo?.rowId === row.id ? activePromo.periodIdx : null}
          parityHintForRow={parityHint?.rowId === row.id ? parityHint : null}
          handleSimEdit={stableHandleSimEdit}
          handleClearEdit={stableHandleClearEdit}
          handleRevertRow={handleRevertRow}
          handleRestoreRow={handleRestoreRow}
          handleApplyParityHint={stableHandleApplyParityHint}
          dismissParityHint={stableDismissParityHint}
          setActivePromo={setActivePromo}
          setPromoAnchor={setPromoAnchor}
          setClearedCells={setClearedCells}
          cellEditModeRef={cellEditModeRef}
        />
      );
    },
    [editedMeasures, clearedCells, originalMeasuresMap, revertSnapshots, activeVisiblePeriodSet, activePeriodColumns, activePastPeriodSet, activePromo, parityHint, stableHandleSimEdit, stableHandleClearEdit, handleRevertRow, handleRestoreRow, stableHandleApplyParityHint, stableDismissParityHint],
  );

  // ── CSV Export ──────────────────────────────────────────────────────────────

  const [exporting, setExporting] = useState<"collapsed" | "expanded" | null>(null);

  const handleExport = useCallback(
    async (mode: "collapsed" | "expanded") => {
      if (exporting) return;
      setExporting(mode);
      try {
        const dateStr = new Date().toISOString().split("T")[0];
        const downloadFilename = `lmnt_shipments_planning_console_${mode}_${dateStr}.csv`;

        // Shared self-documenting parameters preamble (unified with the Pivot
        // export). The backend export transform prepends these rows.
        const COL_LABEL: Record<string, string> = {
          first_receiver: "First Receiver",
          planning_level: "Planning Level",
          planner_name: "Planner",
          sku: "SKU",
          shipping_sku: "Shipping SKU",
          vividly_product: "Vividly Product",
          stick_sku: "Stick SKU",
        };
        const visibleSorted = sortDateColumns(visiblePeriodColumns);
        const metaRows = buildExportMetaRows({
          view: "Planning Console",
          runLabel: selectedRun ? formatRunOptionLabel(selectedRun) : "",
          exportedAt: dateStr,
          mode,
          grain: "Planning Level × SKU",
          measuresShown:
            mode === "collapsed"
              ? getMeasureLabel(params.bottomAggregateBy, "Week")
              : "Full supply walk",
          unitsLabel:
            SKU_GRAIN_OPTIONS.find((o) => o.value === params.skuGrain)?.label ?? params.skuGrain,
          displayBy: "Week",
          dateRange:
            visibleSorted.length > 0
              ? `${visibleSorted[0]} – ${visibleSorted[visibleSorted.length - 1]}`
              : "(all)",
          showActuals: actualsExpanded,
          sortLabel: sortLabel(skuSort, (c) => COL_LABEL[c] ?? c),
          filtersLabel: filters.length
            ? filters
                .map(([col, op, vals]) => `${COL_LABEL[col] ?? col} ${op} [${vals.join(", ")}]`)
                .join("; ")
            : "(none)",
        });

        // Mirror sortedSkuRows' (sku, planning_level) tiebreak — without it,
        // the export's row order diverges from what the user sees (where
        // same-SKU rows always sit contiguous regardless of the active sort).
        // De-dup: if the user already sorts on sku or planning_level, the
        // server applies it before our fallback, so the tiebreak only fills
        // gaps the user didn't sort on.
        const userSort = skuSort.map((sc) => ({ column: sc.column, direction: sc.direction }));
        const userSortCols = new Set(userSort.map((s) => s.column));
        const tiebreak: { column: string; direction: "asc" | "desc" }[] = [];
        if (!userSortCols.has("sku")) tiebreak.push({ column: "sku", direction: "asc" });
        if (!userSortCols.has("planning_level")) tiebreak.push({ column: "planning_level", direction: "asc" });
        const exportParams = {
          ...vtParams,
          mode,
          aggregate_by: params.bottomAggregateBy,
          sort_by: [...userSort, ...tiebreak],
          output_format: "csv",
          download_filename: downloadFilename,
          meta_rows: metaRows,
        };

        const input: CreateMyViewTransformVTInput = {
          transformId: "ui_supply_walk_for_editing_export",
          transformationSettings: JSON.stringify(exportParams),
          runSettings: runSettingsJson || null,
          forceRecalc: true,
        };

        const { data } = await viewTransformsClient.mutate<CreateMyViewTransformVTResult>({
          mutation: CREATE_MY_VIEW_TRANSFORM_VT,
          variables: { input },
          fetchPolicy: "no-cache",
        });

        const result = data?.createMyViewTransform;
        if (!result || result.status === "FAILED") {
          throw new Error(result?.errorMessage || "Export failed");
        }

        if (result.status === "COMPLETED" && result.s3Url) {
          const a = document.createElement("a");
          a.href = result.s3Url;
          a.download = downloadFilename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } catch (err) {
        console.error("CSV export failed:", err);
      } finally {
        setExporting(null);
      }
    },
    [
      exporting,
      vtParams,
      params.bottomAggregateBy,
      params.skuGrain,
      skuSort,
      runSettingsJson,
      selectedRun,
      actualsExpanded,
      filters,
      visiblePeriodColumns,
    ]
  );

  // ── Dynamic sticky-column widths (canvas-measured) ────────────────────────
  // Marked-ready col renders three cohorts: dates (top), SKU codes (bottom),
  // and measure labels (detail rows). Detail labels include a sign-glyph span
  // (~17px) and — on the editable measure only — a revert button (~24px).
  const SIGN_PREFIX_PX = 17;
  const REVERT_BTN_PX = 24;

  const tableWidthsStyle = useMemo<CSSProperties>(() => {
    const firstReceivers = new Set<string>();
    const planningLevels = new Set<string>();
    const skus = new Set<string>();
    for (const r of filteredSkuRows) {
      firstReceivers.add(r.firstReceiver);
      planningLevels.add(r.planningLevel);
      skus.add(r.sku);
    }
    // CHO-678 Phase C: in collapsed mode filteredSkuRows is empty, so size the
    // sticky columns from the summary sku names + any lazily-loaded detail rows
    // too — otherwise the SKU / planning-level columns collapse to the header
    // label width and truncate.
    for (const sr of summarySkuRows) skus.add(sr.sku);
    for (const r of detailSkuRows) {
      firstReceivers.add(r.firstReceiver);
      planningLevels.add(r.planningLevel);
      skus.add(r.sku);
    }

    // Both tables share sticky col 3 and col 4, so each width must fit the
    // longest content from EITHER table at that column index:
    //   col 3: first_receiver (top) ∪ sku (bottom)
    //   col 4: marked_ready dates (top) ∪ planning_level + measure labels (bottom)
    // Measure labels live in the bottom's expanded detail rows but render in
    // col 4, so they're included here for proper sizing.
    const col3W = calculateColumnWidth(
      [...firstReceivers, ...skus],
      "First Receiver",
    );
    const measureLabelItems: WidthInput[] = MEASURE_KEYS.map((k) => ({
      text: getMeasureLabel(k, "Week"),
      extraPx:
        SIGN_PREFIX_PX +
        (k === "simulated_shipments_placed_planner_override" ? REVERT_BTN_PX : 0),
    }));
    const col4W = calculateColumnWidth(
      [...planningLevels, ...measureLabelItems],
      "Marked Ready",
      { max: 500 },
    );

    return {
      "--sku-col-w": `${col3W}px`,
      "--marked-ready-col-w": `${col4W}px`,
    } as CSSProperties;
  }, [filteredSkuRows, summarySkuRows, detailSkuRows]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div css={s.page} style={tableWidthsStyle}>
      <PageHeader
        title={<ShipmentsTabs />}
        actions={
          <>
            <WarningIcon
              count={mappingGaps.count}
              messages={mappingGaps.messages}
              onClick={() => navigate("/data-hub")}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" css={s.exportBtn} disabled={!!exporting}>
                  {exporting ? <Loader2 size={13} css={css`animation: spin 1s linear infinite; @keyframes spin { to { transform: rotate(360deg); } }`} /> : <Download size={13} />}
                  {exporting ? "Exporting\u2026" : "Export"}
                  <ChevronDown size={11} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleExport("collapsed")}>
                  Export Collapsed
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleExport("expanded")}>
                  Export Expanded
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ApplyShipmentEditsButton selectedRun={selectedRun} />
          </>
        }
      />
      <div css={s.runSelectorRow}>{titleAddon}</div>

      <ViewControls loading={lazyDetail ? (summaryVt.loading || summaryVt.refreshing || skuSummaryVt.refreshing || detailVt.refreshing) : (vt.loading || vt.refreshing)}>
        <ViewControls.DisplayBy
          value={params.skuGrain}
          onChange={(v) => {
            const nextGrain = v as SkuGrainValue;
            // Convert selection to the equivalent sku set in the new grain's
            // column. Falls back to clearing when the mapping hasn't loaded
            // yet — preferable to leaving stale values that would push down
            // against the wrong column on the next VT query.
            const nextFilterSku =
              params.filterSku.length === 0
                ? []
                : skuMapping.ready
                ? skuMapping.convert(
                    params.filterSku,
                    GRAIN_TO_SKU_COL[params.skuGrain],
                    GRAIN_TO_SKU_COL[nextGrain],
                  )
                : [];
            setParams({ skuGrain: nextGrain, filterSku: nextFilterSku });
          }}
          options={SKU_GRAIN_OPTIONS}
        />
        <ViewControls.FilterDropdown
          label="SKU"
          values={params.filterSku}
          onChange={(vals) => setParam("filterSku", vals)}
          options={skuFilterOptions}
          floating
        />
        {filterColumns.map((col) => (
          <ViewControls.FilterDropdown
            key={col.col}
            label={col.label}
            values={params[col.param]}
            onChange={(vals) => setParam(col.param, vals)}
            options={col.options}
            floating
          />
        ))}
        <ViewControls.FilterDropdown
          label="Alerts"
          values={params.filterAlertTypes}
          onChange={(vals) => setParam("filterAlertTypes", vals)}
          options={alertOptions}
          multiple={true}
          clearable={true}
          floating
        />
        <label css={s.applyEditsToggle} title="When off, the table shows the engine's raw shipment plan with planner-saved edits hidden.">
          <Switch
            checked={params.applyEdits}
            onCheckedChange={(v) => {
              if (!v && pendingEditCount > 0) {
                setConfirmEngineSwitch(true);
                return;
              }
              setParam("applyEdits", v);
            }}
            aria-label="Apply planner edits"
          />
          Apply planner edits
        </label>
      </ViewControls>
      {!params.applyEdits && (
        <div css={s.engineViewBanner}>
          Engine-only view. Showing the engine's raw plan without saved planner edits.
        </div>
      )}

      {(lazyDetail ? (summaryVt.error ?? skuSummaryVt.error) : vt.error) ? (
        <ErrorState
          error={(lazyDetail ? (summaryVt.error ?? skuSummaryVt.error) : vt.error)!}
          title="Failed to load data"
          description="There was a problem loading the retailer supply data. Try adjusting your filters or refreshing."
          actionLabel="Retry"
          onAction={() => window.location.reload()}
        />
      ) : !lazyDetail && !fullReady && summaryVt.arrowTable && summaryFirstReceiverSummaries.length > 0 ? (
        // ── Summary-mode first paint (CHO-678 OQ3 Variant 2) ───────────────
        // (Non-lazy only — when lazyDetail is on we skip straight to the
        // interactive content branch, which is itself summary-driven.)
        // The lean summary VT has landed but the full per-SKU VT is still
        // backfilling. Paint the top panel read-only from the summary; the
        // bottom panel shows its loading state until fullReady, at which point
        // the branch below takes over with the full-fidelity interactive panels.
        <div css={s.content}>
          <div css={[s.panelCard, s.topPanel]}>
            <div css={s.panelHeader}>
              <div css={s.panelHeaderLeft}>
                <span css={s.panelTitle}>Weeks of Supply by First Receiver</span>
                <span css={s.panelDesc}>Loading detail…</span>
              </div>
            </div>
            <div css={tableStyles.wrap}>
              <DataTable<FirstReceiverSummary>
                data={summaryFirstReceiverSummaries}
                columns={summaryColumns}
                headerGroups={summaryHeaderGroups}
                renderCells={renderSummaryFirstReceiverCells}
                scrollerRef={topScrollRef}
                onScrollerScroll={onTopScroll}
              />
            </div>
          </div>
          <div css={[s.panelCard, s.bottomPanel]}>
            <div css={s.panelHeader}>
              <div css={s.panelHeaderLeftGap}>
                <span css={s.panelTitle}>Supply Walk by Retailer + SKU</span>
                <span css={s.panelDescNoMargin}>
                  {summarySkuRows.length === 0
                    ? "Loading…"
                    : detailVt.loading || detailVt.refreshing
                      ? "Loading detail…"
                      : "Expand a SKU for detail"}
                </span>
              </div>
            </div>
            {summarySkuRows.length > 0 ? (
              // Collapsed SKU header rows from the sku-summary VT. The chevron is
              // live: expanding a SKU fires the lazy detail VT (detailVt) and its
              // PL rows + measure walk render beneath — same DataTable machinery
              // as the full path (CHO-678 OQ3 Phase C). detailVt.loading shows a
              // subtle refresh hint in the panel header.
              <div css={tableStyles.wrap}>
                <DataTable<BottomItem>
                  data={bottomItems}
                  getRowId={getSkuRowId}
                  columns={skuColumns}
                  headerGroups={skuStatusHeaderGroups}
                  renderCells={renderSkuCells}
                  getDetailKeys={getSkuDetailKeys}
                  renderDetailRow={renderSkuDetailRow}
                  sort={skuSort}
                  onSort={handleSkuSort}
                  getRowBg={(item, isExpanded) => {
                    if (item.kind === "header") {
                      return item.isOpen ? theme.colors.blue50 : theme.colors.background;
                    }
                    return isExpanded ? theme.colors.gray200 : theme.colors.background;
                  }}
                  getDetailMultiRowBg={(_, mk) =>
                    mk === params.bottomAggregateBy ? theme.colors.gray50 : theme.colors.background
                  }
                  scrollerRef={bottomScrollRef}
                  onScrollerScroll={onBottomScroll}
                />
              </div>
            ) : (
              <LoadingState message="Loading supply walk detail..." size="small" minHeight={200} />
            )}
          </div>
        </div>
      ) : !runSettingsJson || (lazyDetail ? (!summaryVt.arrowTable && !skuSummaryVt.arrowTable) : vt.loading) ? (
        // Cold load only (no arrowTable yet). A `refreshing` re-fetch — e.g. the
        // post-save forceRecalc — deliberately falls through to the content
        // branch so the grid stays mounted and visible (CHO-693): blanking it
        // here both blocked the page for ~10s and destroyed DataTable-internal
        // (per-PL) expansion + scroll position. The refresh overlay below
        // covers the still-mounted content instead. In lazyDetail mode EITHER
        // summary VT landing reveals the content (each table fills when its own
        // VT lands) so a slow FR-summary cold start doesn't block the bottom.
        <LoadingState message="Loading data..." size="small" minHeight={200} />
      ) : (lazyDetail ? (summaryFirstReceiverSummaries.length === 0 && summarySkuRows.length === 0) : filteredSkuRows.length === 0) ? (
        <EmptyState
          title="No data found"
          description="No results match your current filters. Try adjusting your selections."
          minHeight={200}
        />
      ) : (
        <div css={s.contentWrap}>
        <div css={s.content}>
          {/* ── Panel 1: First Receiver Summary ────────────────────────── */}
          <div css={[s.panelCard, selectedFirstReceiver ? s.topPanelCollapsed : s.topPanel]}>
            <div css={s.panelHeader}>
              <div css={s.panelHeaderLeft}>
                <span css={s.panelTitle}>Weeks of Supply by First Receiver</span>
                {selectedFirstReceiver ? (
                  <span css={s.drilldownBadge}>
                    {selectedFirstReceiver}
                    <span css={s.drilldownClear} onClick={() => setSelectedFirstReceiver(null)}>
                      clear
                    </span>
                  </span>
                ) : (
                  <span css={s.panelDesc}>Click a first receiver to drill down</span>
                )}
              </div>
              {/* <ViewControls.AggregateBy
                value={params.topAggregateBy}
                onChange={(v) => setParam("topAggregateBy", v)}
                options={AGGREGATE_OPTIONS}
                label="Show"
              /> */}
              {selectedFirstReceivers.size > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  disabled={markingReady}
                  onClick={markSelectedAsReady}
                  css={s.markReadyBtn}
                >
                  <CheckCheck size={13} />
                  {markingReady
                    ? "Marking..."
                    : `Mark ${selectedFirstReceivers.size} as Ready`}
                </Button>
              )}
            </div>
            {selectedFirstReceiver ? (
              <div css={css`overflow-x: auto;`} ref={topScrollRef} onScroll={onTopScroll}>
                <table css={tableStyles.table}>
                  <thead css={tableStyles.thead}>
                    {statusHeaderGroups && (
                      <tr>
                        {statusHeaderGroups.map((g, i) => (
                          <TableHead key={i} colSpan={g.colSpan} css={[tableStyles.th, tableStyles.thGroupRow, g.css]}>
                            {g.label}
                          </TableHead>
                        ))}
                      </tr>
                    )}
                    <tr>
                      {retailerColumns.map((col) => (
                        <TableHead key={col.key} css={[tableStyles.th, col.css]}>
                          {col.header}
                        </TableHead>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFirstReceiverSummaries.map((row) => (
                      <tr
                        key={row.firstReceiver}
                        css={css`
                          cursor: pointer;
                          border-bottom: 1px solid ${theme.colors.border};
                          background: ${theme.colors.blue50};
                          &:hover { background: ${theme.colors.blue50}; }
                        `}
                        onClick={() => setSelectedFirstReceiver(null)}
                      >
                        {renderFirstReceiverCells(row)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div css={tableStyles.wrap}>
                <DataTable<FirstReceiverSummary>
                  data={sortedFirstReceiverSummaries}
                  columns={retailerColumns}
                  headerGroups={statusHeaderGroups}
                  renderCells={renderFirstReceiverCells}
                  isRowSelected={(row) => row.firstReceiver === selectedFirstReceiver}
                  sort={topSort}
                  onSort={handleTopSort}
                  scrollerRef={topScrollRef}
                  onScrollerScroll={onTopScroll}
                />
              </div>
            )}
          </div>

          {/* ── Panel 2: SKU Supply Walk ────────────────────────────────── */}
          <div css={[s.panelCard, s.bottomPanel]}>
            <div css={s.panelHeader}>
              <div css={s.panelHeaderLeftGap}>
                <span css={s.panelTitle}>Supply Walk by Retailer + SKU</span>
                {selectedFirstReceiver ? (
                  <span css={s.drilldownBadge}>
                    {selectedFirstReceiver}
                    <span css={s.drilldownClear} onClick={() => setSelectedFirstReceiver(null)}>
                      clear
                    </span>
                  </span>
                ) : (
                  <span css={s.panelDescNoMargin}>Showing all {visibleSkuRows.length} SKUs</span>
                )}
              </div>
              <div css={css`display: flex; align-items: center; gap: 0.75rem;`}>
                <ViewControls.AggregateBy
                  value={params.bottomAggregateBy}
                  onChange={(v) => setParam("bottomAggregateBy", v)}
                  options={AGGREGATE_OPTIONS}
                  label="Show"
                />
                {/* Bulk-upload edits from CSV. Disabled while there are
                    unsaved manual edits — wrap the button in a span so the
                    title attribute fires hover even when disabled. */}
                <span
                  title={
                    !params.applyEdits
                      ? "Switch back to Apply planner edits to upload"
                      : pendingEditCount > 0
                      ? "Save or discard your unsaved edits before uploading from CSV"
                      : undefined
                  }
                >
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!params.applyEdits || isSaving || pendingEditCount > 0 || isCsvUploading}
                    onClick={() => csvFileInputRef.current?.click()}
                    css={css`
                      gap: 0.375rem;
                      font-size: 0.75rem;
                      padding: 0.25rem 0.75rem;
                      height: auto;
                    `}
                  >
                    {isCsvUploading ? (
                      <Loader2
                        size={13}
                        css={css`animation: spin 1s linear infinite; @keyframes spin { to { transform: rotate(360deg); } }`}
                      />
                    ) : (
                      <Upload size={13} />
                    )}
                    {isCsvUploading ? "Uploading…" : "Upload from CSV"}
                  </Button>
                </span>
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleCsvUpload(f);
                    e.target.value = "";
                  }}
                />
                {(pendingEditCount > 0 || isSaving) && (
                  <Button
                    variant="secondaryDark"
                    size="sm"
                    disabled={isSaving}
                    onClick={persistAllEdits}
                    css={css`
                      gap: 0.375rem;
                      font-size: 0.75rem;
                      padding: 0.25rem 0.75rem;
                      height: auto;
                    `}
                  >
                    <Save size={13} />
                    {isSaving
                      ? "Saving..."
                      : `Save ${pendingEditCount} Change${pendingEditCount === 1 ? "" : "s"}`}
                  </Button>
                )}
              </div>
            </div>

            <div css={tableStyles.wrap}>
              <DataTable<BottomItem>
                data={bottomItems}
                getRowId={getSkuRowId}
                columns={skuColumns}
                headerGroups={skuStatusHeaderGroups}
                renderCells={renderSkuCells}
                getDetailKeys={getSkuDetailKeys}
                renderDetailRow={renderSkuDetailRow}
                sort={skuSort}
                onSort={handleSkuSort}
                getRowBg={(item, isExpanded) => {
                  if (item.kind === "header") {
                    // Expanded SKU header gets a blue tint so it reads as
                    // the parent of the PL rows below — clearer separator
                    // than the gray100 sibling-shade used elsewhere.
                    return item.isOpen ? theme.colors.blue50 : theme.colors.background;
                  }
                  return isExpanded ? theme.colors.gray200 : theme.colors.background;
                }}
                getDetailMultiRowBg={(_, mk) =>
                  mk === params.bottomAggregateBy ? theme.colors.gray50 : theme.colors.background
                }
                scrollerRef={bottomScrollRef}
                onScrollerScroll={onBottomScroll}
              />
            </div>
          </div>
        </div>
        {(lazyDetail ? (summaryVt.refreshing || skuSummaryVt.refreshing || detailVt.refreshing) : vt.refreshing) && (
          // Non-blocking refresh affordance (CHO-693). A small corner pill — it
          // does NOT cover or block the grid, which stays fully visible (showing
          // optimistic edited values) so the planner keeps their place.
          <div css={s.refreshPill} aria-live="polite">
            <span css={s.refreshPillSpinner} />
            {isSaving ? "Saving changes…" : "Updating…"}
          </div>
        )}
        </div>
      )}
      <AlertDialog open={confirmEngineSwitch} onOpenChange={setConfirmEngineSwitch}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved planner edits</AlertDialogTitle>
            <AlertDialogDescription>
              You have {pendingEditCount} unsaved {pendingEditCount === 1 ? "edit" : "edits"}. Save them before switching to engine-only view, or discard to switch without saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="secondary"
              onClick={() => {
                setEditedMeasures(new Map());
                setClearedCells(new Map());
                setRevertSnapshots(new Map());
                setConfirmEngineSwitch(false);
                setParam("applyEdits", false);
              }}
            >
              Discard &amp; switch
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => {
                await persistAllEdits();
                setConfirmEngineSwitch(false);
                setParam("applyEdits", false);
              }}
            >
              Save &amp; switch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
